import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaClipboardList, FaTasks, FaCalendarAlt, FaLink, FaPlus, FaChevronRight, FaChevronDown, FaProjectDiagram, FaTimes, FaExclamationTriangle, FaCode, FaExternalLinkAlt, FaCopy, FaDownload, FaUserCircle, FaBook } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ClosureCommentModal from '../../../components/ClosureCommentModal';
import TaskHistory from '../../../components/TaskHistory';
import { getBlockers } from '../../../utils/idHelpers';
import { getStatusColour } from './gantt/GanttUtils';
import { apiFetch } from '../../../utils/apiAdmin';
import { formatRelativeDay, getEffectiveRelativeDateRange } from '../../../utils/templateDateUtils';
import EffortInput from '../../../components/EffortInput';
// ⚠️ The lane test for EFFORT ONLY. This file has its own `isTeamTask` for
// permissions; the two are deliberately not the same predicate - see the Effort
// section's comment.
import { isClientLaneTask } from '../../../utils/effortDisplay';

// Helper to format date as UK format (DD/MM/YYYY)
const formatDateUK = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

// Helper to check if date is overdue
const isOverdue = (dateString) => {
    if (!dateString) return false;
    const dueDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < today;
};

// Helper to check if date is within 2 days
const isWithin2Days = (dateString) => {
    if (!dateString) return false;
    const dueDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    const diffDays = (dueDate - today) / (1000 * 60 * 60 * 24);
    return diffDays >= 0 && diffDays <= 2;
};

// Helper to get due date text colour class
// Red: overdue, Orange: within 2 days, Grey: more than 2 days away
const getDueDateColour = (dateString) => {
    if (!dateString) return '';
    if (isOverdue(dateString)) return 'bg-red-100 text-red-700';
    if (isWithin2Days(dateString)) return 'bg-orange-100 text-orange-700';
    return 'bg-gray-100 text-gray-700'; // Far in future - not urgent
};

// Helper to get start date text colour class
// Grey: more than 3 days away, Orange: within 3 days, Green: started (today or past)
const getStartDateColour = (dateString) => {
    if (!dateString) return 'bg-gray-100 text-gray-500';
    const startDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    startDate.setHours(0, 0, 0, 0);
    const diffDays = (startDate - today) / (1000 * 60 * 60 * 24);

    if (diffDays <= 0) return 'bg-green-100 text-green-700'; // Started (today or past)
    if (diffDays <= 3) return 'bg-orange-100 text-orange-700'; // Within 3 days
    return 'bg-gray-100 text-gray-500'; // More than 3 days away
};

// Helper to get due date input colour class
const getDueDateInputColour = (dateString) => {
    if (!dateString) return 'border-gray-300';
    if (isOverdue(dateString)) return 'border-red-300 bg-red-50 text-red-700';
    if (isWithin2Days(dateString)) return 'border-orange-300 bg-orange-50 text-orange-700';
    return 'border-gray-300 bg-gray-100 text-gray-700'; // Far in future - not urgent (doc scheme: grey)
};

// Helper to format date for input (YYYY-MM-DD)
// Names the thing that failed to save, so the alert is not always "changes"
// (Bill, 2026-08-03). Anything unlisted keeps the generic wording.
const FIELD_LABEL = {
    links: 'the link',
    dates: 'the dates'
};

const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
};

// Helper to add days to today
const addDaysToToday = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
};

// Calculate effective date range from subtasks for main tasks
const getEffectiveDateRange = (task, allTasks) => {
    const subtasks = allTasks.filter(t => String(t.mainTaskId) === String(task.taskId) && t.projectId === task.projectId);
    if (subtasks.length === 0) {
        return { start: task.startDate, end: task.dueDate };
    }

    // Find earliest start date from subtasks
    const startDates = subtasks
        .map(st => st.startDate)
        .filter(Boolean)
        .map(d => new Date(d));
    const start = startDates.length > 0
        ? new Date(Math.min(...startDates)).toISOString().split('T')[0]
        : null;

    // Find latest due date from subtasks
    const endDates = subtasks
        .map(st => st.dueDate)
        .filter(Boolean)
        .map(d => new Date(d));
    const end = endDates.length > 0
        ? new Date(Math.max(...endDates)).toISOString().split('T')[0]
        : null;

    return { start, end };
};

// Parse dependsOn string into array: "12,13:SS" -> [{taskId, type}]
const parseDependencies = (task) => {
    if (!task) return [];
    const dependsOn = task.dependsOn;
    if (!dependsOn) return [];

    return dependsOn.split(',').map(d => d.trim()).filter(d => d).map(dep => {
        if (dep.includes(':')) {
            const [taskId, type] = dep.split(':').map(s => s.trim());
            return { taskId, type: type.toUpperCase() };
        }
        return { taskId: dep, type: 'FS' };
    });
};


// Helper to extract link pairs from task.links array
const extractLinkPairs = (task) => {
    if (!task) return [];
    const links = task.links;
    if (!Array.isArray(links)) return [];
    return links.map((link, idx) => ({
        number: idx + 1,
        url: link.url,
        description: link.description
    }));
};

// Agenda Editor with markdown toolbar
const AgendaEditor = ({ value, onChange, onSave, disabled, saving, originalValue, headerless = false }) => {
    const textareaRef = useRef(null);
    const hasChanges = value !== originalValue;

    const insertMarkdown = (prefix, suffix = '') => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = value || '';
        const selectedText = text.substring(start, end);

        // Find the start of the current line
        const lineStart = text.lastIndexOf('\n', start - 1) + 1;
        const beforeLine = text.substring(0, lineStart);
        const afterSelection = text.substring(end);

        let newText;
        let newCursorPos;

        if (prefix === '- ' || prefix === '1. ' || prefix === '- [ ] ') {
            // For list items, insert at start of line
            const lineContent = text.substring(lineStart, end);
            newText = beforeLine + prefix + lineContent + afterSelection;
            newCursorPos = lineStart + prefix.length + (end - lineStart);
        } else {
            // For inline formatting (bold, italic)
            newText = text.substring(0, start) + prefix + selectedText + suffix + afterSelection;
            newCursorPos = start + prefix.length + selectedText.length + suffix.length;
        }

        onChange(newText);

        // Restore cursor position after state update
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
    };

    // Handle Enter key to auto-continue lists
    const handleKeyDown = (e) => {
        if (e.key !== 'Enter') return;

        const textarea = textareaRef.current;
        if (!textarea) return;

        const text = value || '';
        const cursorPos = textarea.selectionStart;

        // Find current line
        const lineStart = text.lastIndexOf('\n', cursorPos - 1) + 1;
        const lineEnd = text.indexOf('\n', cursorPos);
        const currentLine = text.substring(lineStart, lineEnd === -1 ? text.length : lineEnd);

        // Check for list prefixes
        let nextPrefix = '';

        // Bullet list: "- "
        if (currentLine.match(/^- (?!\[[ x]\])/)) {
            nextPrefix = '- ';
        }
        // Checkbox: "- [ ] " or "- [x] "
        else if (currentLine.match(/^- \[[ x]\] /)) {
            nextPrefix = '- [ ] ';
        }
        // Numbered list: "1. ", "2. ", etc.
        else if (currentLine.match(/^(\d+)\. /)) {
            const num = parseInt(currentLine.match(/^(\d+)\. /)[1], 10);
            nextPrefix = `${num + 1}. `;
        }

        // If we found a list prefix, handle it
        if (nextPrefix) {
            e.preventDefault();

            // Check if current line is empty (just the prefix)
            const lineContent = currentLine.replace(/^(- \[[ x]\] |- |\d+\. )/, '');
            if (lineContent.trim() === '') {
                // Empty list item - remove the prefix and don't continue
                const newText = text.substring(0, lineStart) + '\n' + text.substring(cursorPos);
                onChange(newText);
                setTimeout(() => {
                    textarea.setSelectionRange(lineStart + 1, lineStart + 1);
                }, 0);
            } else {
                // Continue the list
                const beforeCursor = text.substring(0, cursorPos);
                const afterCursor = text.substring(cursorPos);
                const newText = beforeCursor + '\n' + nextPrefix + afterCursor;
                const newCursorPos = cursorPos + 1 + nextPrefix.length;

                onChange(newText);
                setTimeout(() => {
                    textarea.setSelectionRange(newCursorPos, newCursorPos);
                }, 0);
            }
        }
    };

    const toolbarButtons = [
        { icon: '•', title: 'Bullet list', action: () => insertMarkdown('- ') },
        { icon: '1.', title: 'Numbered list', action: () => insertMarkdown('1. ') },
        { icon: '☐', title: 'Checkbox', action: () => insertMarkdown('- [ ] ') },
        { icon: 'B', title: 'Bold', action: () => insertMarkdown('**', '**'), className: 'font-bold' },
        { icon: 'I', title: 'Italic', action: () => insertMarkdown('_', '_'), className: 'italic' },
    ];

    return (
        <div className="flex flex-col h-full">
            {!headerless && (
                <div className="flex items-center justify-between p-2 bg-gray-100 rounded-t-lg border border-b-0 border-gray-300">
                    <h3 className="text-sm font-medium text-gray-700">Agenda</h3>
                    <div className="flex items-center gap-1">
                        {toolbarButtons.map((btn, idx) => (
                            <button
                                key={idx}
                                type="button"
                                onClick={btn.action}
                                disabled={disabled}
                                title={btn.title}
                                className={`px-2 py-1 text-sm text-gray-700 hover:bg-gray-200 rounded disabled:opacity-50 ${btn.className || ''}`}
                            >
                                {btn.icon}
                            </button>
                        ))}
                        <div className="w-px h-5 bg-gray-300 mx-1"></div>
                        <button
                            type="button"
                            onClick={() => onSave(value)}
                            disabled={disabled || saving || !hasChanges}
                            className="px-2 py-1 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        >
                            {saving ? (
                                <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : 'Save'}
                        </button>
                    </div>
                </div>
            )}
            {headerless && (
                <div className="flex items-center justify-end p-2 bg-yellow-50 border border-b-0 border-yellow-300 gap-1">
                    {toolbarButtons.map((btn, idx) => (
                        <button
                            key={idx}
                            type="button"
                            onClick={btn.action}
                            disabled={disabled}
                            title={btn.title}
                            className={`px-2 py-1 text-sm text-gray-700 hover:bg-yellow-100 rounded disabled:opacity-50 ${btn.className || ''}`}
                        >
                            {btn.icon}
                        </button>
                    ))}
                    <div className="w-px h-5 bg-yellow-300 mx-1"></div>
                    <button
                        type="button"
                        onClick={() => onSave(value)}
                        disabled={disabled || saving || !hasChanges}
                        className="px-2 py-1 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                        {saving ? (
                            <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : 'Save'}
                    </button>
                </div>
            )}
            <textarea
                ref={textareaRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                className={`flex-1 w-full px-3 py-2 border ${headerless ? 'border-yellow-300' : 'border-gray-300'} rounded-b-lg rounded-t-none focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none text-sm font-mono`}
                placeholder="Enter meeting agenda items..."
            />
        </div>
    );
};

// Tree/accordion dropdown for selecting a relative day offset (W{n}D{n})
const RelativeDatePicker = ({ value, onChange, disabled, className }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [expandedWeek, setExpandedWeek] = useState(null);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);

    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    // Auto-expand the week containing the current value on open
    const handleOpen = () => {
        if (disabled) return;
        if (value != null) {
            setExpandedWeek(Math.ceil(value / 7));
        }
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setDropdownPos({ top: rect.bottom + 4, left: rect.left });
        }
        setIsOpen(true);
    };

    // Close on outside click
    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                buttonRef.current && !buttonRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const handleDaySelect = (weekNumber, dayNumber) => {
        const offset = (weekNumber - 1) * 7 + dayNumber;
        onChange(offset);
        setIsOpen(false);
    };

    const currentWeek = value != null ? Math.ceil(value / 7) : null;
    const currentDayInWeek = value != null ? ((value - 1) % 7) + 1 : null;

    return (
        <div className={`relative ${className}`}>
            <button
                ref={buttonRef}
                type="button"
                onClick={handleOpen}
                disabled={disabled}
                className={`px-2 py-1 border rounded text-sm text-left w-full ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer hover:border-gray-400'}`}
            >
                {value != null ? formatRelativeDay(value) : <span className="text-gray-400 italic">Not set</span>}
            </button>
            {isOpen && (
                <div ref={dropdownRef} className="fixed z-[9999] bg-white border rounded shadow-lg overflow-y-auto w-48 max-h-[640px]" style={{ top: dropdownPos.top, left: dropdownPos.left }}>
                    {Array.from({ length: 52 }, (_, i) => i + 1).map(weekNum => (
                        <div key={weekNum}>
                            <button
                                type="button"
                                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center justify-between ${expandedWeek === weekNum ? 'bg-[#d1fae5] text-green-700 font-medium' : ''}`}
                                onClick={() => setExpandedWeek(expandedWeek === weekNum ? null : weekNum)}
                            >
                                <span>Week {weekNum}</span>
                                {expandedWeek === weekNum ? <FaChevronDown className="w-3 h-3 text-gray-400" /> : <FaChevronRight className="w-3 h-3 text-gray-400" />}
                            </button>
                            {expandedWeek === weekNum && (
                                <div className="pl-4 border-l-2 border-[#d1fae5] ml-3">
                                    {dayNames.map((dayName, idx) => {
                                        const dayNum = idx + 1;
                                        const isWeekend = dayNum === 6 || dayNum === 7;
                                        const isSelected = currentWeek === weekNum && currentDayInWeek === dayNum;
                                        return (
                                            <button
                                                key={dayNum}
                                                type="button"
                                                className={`w-full text-left px-3 py-1 text-sm ${isSelected ? 'bg-[#d1fae5] text-green-700 font-medium' : isWeekend ? 'text-gray-400 hover:bg-gray-50' : 'text-gray-700 hover:bg-gray-50'}`}
                                                onClick={() => handleDaySelect(weekNum, dayNum)}
                                            >
                                                D{dayNum} ({dayName})
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const TaskDetailsModal = ({
    task,
    isOpen,
    onClose,
    onUpdate,
    onDelete,
    onCommentAdd,
    onCommentEdit,
    onCommentDelete,
    onSubtaskCreate,
    onSubtaskClick,
    isLoading,
    projectName,
    columns,
    taskStatuses,
    teamSwimlaneId,
    members,
    tasks,
    lead,
    savingChanges,
    savingField,
    addingComment,
    editingComment,
    deletingComment,
    creatingSubtask,
    projectMembers,
    onOpenAssignmentEditor,
    readOnly = false,
    isAdmin: isAdminProp = false,
    blockedTasksMap = new Map(),
    adminTeamMembers = [],
    isTemplateMode = false,
    showScripts = false,
    // TME Wiki pages linked to this task (TWK-23, §9.4). A SEPARATE prop from
    // showScripts, deliberately: the two sections are the same shape today and
    // one flag would mean they can never diverge without a rename. The CLIENT
    // PORTAL PASSES NEITHER, so no client render fires the fetch below - and
    // tests/tme-wiki-client-isolation.test.js asserts that, not the intention.
    showWikiLinks = false,
    // D7 (LIV-16c). A counter the HOST bumps when a scripts_updated arrives, so
    // the linked-script list below refreshes without this modal opening a
    // socket of its own - `fetchTaskScripts` is this component's own callback
    // and cannot be reached from a parent.
    //
    // ⛔ IT DEFAULTS TO A CONSTANT, AND THAT IS LOAD-BEARING. ClientView and the
    // client portal pass nothing; a default that changed between renders would
    // refetch on every render there.
    scriptsRefreshKey = 0
}) => {
    const [editedTaskData, setEditedTaskData] = useState(null);
    const [newComment, setNewComment] = useState('');
    const [showSubtaskForm, setShowSubtaskForm] = useState(false);
    const [hideCompletedSubtasks, setHideCompletedSubtasks] = useState(() => {
        return localStorage.getItem('hideCompletedSubtasks') === 'true';
    });
    const [newSubtask, setNewSubtask] = useState(() => {
        if (isTemplateMode) {
            return { subject: '', description: '', assignedUsers: [], relativeStart: 1, relativeEnd: 5, isMeeting: false, meetingType: '' };
        }
        const today = new Date().toISOString().split('T')[0];
        return { subject: '', description: '', assignedUsers: [], start: today, dueDate: today, isMeeting: false, meetingType: '' };
    });

    // Persist hide completed subtasks preference
    useEffect(() => {
        localStorage.setItem('hideCompletedSubtasks', hideCompletedSubtasks.toString());
    }, [hideCompletedSubtasks]);

    const [showDeletedComments, setShowDeletedComments] = useState(false);
    // Recorded history, admin portal only (Part F of the audit page plan).
    // Collapsed by default and persisted, like every other toggle in the portal.
    const [historyOpen, setHistoryOpen] = useState(
        () => localStorage.getItem('adminTaskHistoryOpen') === 'true'
    );
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editedCommentText, setEditedCommentText] = useState('');
    const [editingDescription, setEditingDescription] = useState(false);
    const [showAssignmentEditor, setShowAssignmentEditor] = useState(false);
    const [assignmentEditorClosing, setAssignmentEditorClosing] = useState(false);
    const [subtaskFormClosing, setSubtaskFormClosing] = useState(false);
    const [descriptionEditorClosing, setDescriptionEditorClosing] = useState(false);
    const [showCommentForm, setShowCommentForm] = useState(false);
    const [commentFormClosing, setCommentFormClosing] = useState(false);
    const [meetingExpanded, setMeetingExpanded] = useState('agenda'); // 'agenda' or 'comments' - which panel is expanded
    const prevTaskIdRef = React.useRef(null);

    // Set meeting default panel based on closed status
    useEffect(() => {
        if (task?.isMeeting) {
            setMeetingExpanded(task.isClosed ? 'comments' : 'agenda');
        }
    }, [task?.taskId, task?.isMeeting, task?.isClosed]);

    // SharePoint links state
    const [linksExpanded, setLinksExpanded] = useState(() => {
        return localStorage.getItem('taskLinksExpanded') === 'true';
    });
    const [editingLinkNumber, setEditingLinkNumber] = useState(null);
    const [linkEditData, setLinkEditData] = useState({ url: '', description: '' });
    const [savingLinks, setSavingLinks] = useState(false);

    // Dependencies state
    const [depsExpanded, setDepsExpanded] = useState(() => {
        return localStorage.getItem('taskDepsExpanded') === 'true';
    });

    // Blocked hover popup - fixed position so scroll containers cannot clip it
    const [blockedHover, setBlockedHover] = useState(null); // { x, y, blockers }
    const showBlockedHover = (e, blockers) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setBlockedHover({ x: rect.left + rect.width / 2, y: rect.top - 8, blockers });
    };
    const hideBlockedHover = () => setBlockedHover(null);

    // Closure comment modal state
    const [showClosureModal, setShowClosureModal] = useState(false);
    const [closurePending, setClosurePending] = useState(null); // { statusId }
    const [isSubmittingClosure, setIsSubmittingClosure] = useState(false);

    // Scripts section state (template mode only)
    const [scriptsExpanded, setScriptsExpanded] = useState(() => {
        return localStorage.getItem('taskScriptsExpanded') === 'true';
    });
    const [taskScripts, setTaskScripts] = useState([]);
    const [loadingScripts, setLoadingScripts] = useState(false);
    const [showLinkScriptModal, setShowLinkScriptModal] = useState(false);
    const [availableScripts, setAvailableScripts] = useState([]);
    const [loadingAvailableScripts, setLoadingAvailableScripts] = useState(false);
    const [linkingScript, setLinkingScript] = useState(null);
    const [unlinkingScript, setUnlinkingScript] = useState(null);
    const [copyFeedback, setCopyFeedback] = useState(null);

    // TME Wiki section state (TWK-23, §9.4)
    const [wikiExpanded, setWikiExpanded] = useState(() => {
        return localStorage.getItem('taskWikiExpanded') === 'true';
    });
    const [taskWikiPages, setTaskWikiPages] = useState([]);
    const [loadingWikiPages, setLoadingWikiPages] = useState(false);
    const [showLinkWikiModal, setShowLinkWikiModal] = useState(false);
    const [availableWikiPages, setAvailableWikiPages] = useState([]);
    const [loadingAvailableWikiPages, setLoadingAvailableWikiPages] = useState(false);
    const [linkingWikiPage, setLinkingWikiPage] = useState(null);
    const [unlinkingWikiPage, setUnlinkingWikiPage] = useState(null);

    // Persist deps expanded preference
    useEffect(() => {
        localStorage.setItem('taskDepsExpanded', depsExpanded.toString());
    }, [depsExpanded]);

    // Persist links expanded preference
    useEffect(() => {
        localStorage.setItem('taskLinksExpanded', linksExpanded.toString());
    }, [linksExpanded]);

    // Persist scripts expanded preference
    useEffect(() => {
        localStorage.setItem('taskScriptsExpanded', scriptsExpanded.toString());
    }, [scriptsExpanded]);

    // Fetch scripts for task - by task name (subject) for v2 name-based linking
    // ⚠️ `silent` (LIV, D1) is the D7 bump's mode: no spinner, and the list KEPT
    // when a refresh nobody asked for fails - the mount path blanks it, which is
    // right on mount and wrong for a background refetch.
    const fetchTaskScripts = useCallback(async ({ silent = false } = {}) => {
        if (!(isTemplateMode || showScripts) || !task?.subject) return;

        const taskName = encodeURIComponent(task.subject.trim());
        if (!silent) setLoadingScripts(true);
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/task-scripts-by-subject/${taskName}`);
            if (response.ok) {
                const data = await response.json();
                setTaskScripts(data.scripts || []);
            } else {
                console.error('Failed to fetch task scripts:', response.status);
                if (!silent) setTaskScripts([]);
            }
        } catch (err) {
            console.error('Error fetching task scripts:', err);
            if (!silent) setTaskScripts([]);
        } finally {
            if (!silent) setLoadingScripts(false);
        }
    }, [isTemplateMode, showScripts, task?.subject]);

    // Which key this effect last ran for. A run caused by the key CHANGING is a
    // D7 bump and goes silent; a run caused by anything else is a load.
    const lastScriptsRefreshKeyRef = useRef(scriptsRefreshKey);
    useEffect(() => {
        const bumped = scriptsRefreshKey !== lastScriptsRefreshKeyRef.current;
        lastScriptsRefreshKeyRef.current = scriptsRefreshKey;
        fetchTaskScripts({ silent: bumped });
        // D7: `scriptsRefreshKey` joins the SAME effect rather than getting one
        // of its own, so a bump refreshes exactly what a mount does.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchTaskScripts, scriptsRefreshKey]);

    // Persist TME Wiki expanded preference
    useEffect(() => {
        localStorage.setItem('taskWikiExpanded', wikiExpanded.toString());
    }, [wikiExpanded]);

    /**
     * TME Wiki pages linked to this task, BY NORMALISED TASK NAME (D4).
     *
     * ⚠️ NEITHER PROP MEANS NO FETCH AT ALL. This is the client-portal case: the
     * portal passes neither `isTemplateMode` nor `showWikiLinks`, so an internal
     * route is never called from a client's browser, and the isolation suite
     * asserts the absence of the call rather than the absence of the section.
     *
     * ⚠️ A RENAME ORPHANS THE LINK (D18, R7), exactly as it already does for
     * script links. Accepted: carrying it would mean granting two more Lambdas
     * write access to these tables.
     */
    const fetchTaskWikiPages = useCallback(async () => {
        if (!(isTemplateMode || showWikiLinks) || !task?.subject) return;

        const taskName = encodeURIComponent(task.subject.trim());
        setLoadingWikiPages(true);
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/tme-wiki/task-pages/${taskName}`);
            if (response.ok) {
                const data = await response.json();
                setTaskWikiPages(data.pages || []);
            } else {
                console.error('Failed to fetch TME wiki pages for task:', response.status);
                setTaskWikiPages([]);
            }
        } catch (err) {
            console.error('Error fetching TME wiki pages for task:', err);
            setTaskWikiPages([]);
        } finally {
            setLoadingWikiPages(false);
        }
    }, [isTemplateMode, showWikiLinks, task?.subject]);

    useEffect(() => {
        fetchTaskWikiPages();
    }, [fetchTaskWikiPages]);

    // Initialize editedTaskData when task changes
    React.useEffect(() => {
        if (task) {
            setEditedTaskData({
                subject: task.subject,
                description: task.description || '',
                status: task.mainTaskId ? task.status : task.epicId,
                ...(isTemplateMode
                    ? { relativeStart: task.relativeStart || null, relativeEnd: task.relativeEnd || null }
                    : { due_date: task.dueDate || null, start: task.startDate || null }
                ),
                isSubtask: !!task.mainTaskId
            });
            // Only reset UI state when opening a different task, not when same task updates
            if (prevTaskIdRef.current !== task.taskId) {
                setShowAssignmentEditor(false);
                prevTaskIdRef.current = task.taskId;
            }
        }
    }, [task]);

    if (!isOpen || !task) return null;

    // Check if any operation is in progress - disable close while working
    const isOperationInProgress = savingChanges || addingComment || editingComment || deletingComment || creatingSubtask;

    // For subtasks, check if PARENT is in team swimlane
    let isTeamTask;
    if (task.mainTaskId && tasks) {
        const parentTask = tasks.find(t => String(t.taskId) === String(task.mainTaskId) && t.projectId === task.projectId);
        if (parentTask) {
            isTeamTask = parentTask.swimlaneId === teamSwimlaneId ||
            (parentTask.swimlaneId === null && teamSwimlaneId === parentTask.project?.default_swimlane);
        } else {
            isTeamTask = false;
        }
    } else {
        // For main tasks
        isTeamTask = task.swimlaneId === teamSwimlaneId ||
        (task.swimlaneId === null && teamSwimlaneId === task.project?.default_swimlane);
    }

    // Permission Checks
    const userEmail = lead?.email;
    // Editing is assignment-based.
    const canUserEditByAssignment = (item) => {
        if (!lead || !userEmail) return false;
        if (lead.clientRole === 'admin') return true;
        if (item.assignedTo && String(item.assignedTo) === String(userEmail)) {
            return true;
        }
        if (item.assignedUsers && item.assignedUsers.some(u => String(u) === String(userEmail))) {
            return true;
        }
        return false;
    };

    // Deleting is ownership-based, and a different question from editing:
    // a basic user deletes only subtasks they created, and never a main task.
    // Client admins keep the blanket grant they have always had.
    const canUserDelete = (item) => {
        if (!lead || !userEmail) return false;
        if (lead.clientRole === 'admin') return true;
        if (!item.mainTaskId) return false;
        return !!item.createdBy && item.createdBy === lead.id;
    };

    // Permission checks
    // isAdminProp = true means admin portal (can edit all), clientRole = 'admin' means client admin (Team swimlane only)
    const isClientAdmin = lead?.clientRole === 'admin';
    const isAssignedToUser = canUserEditByAssignment(task);

    // Check if user is assigned to parent task (for subtask editing)
    let isAssignedToParent = false;
    if (task.mainTaskId && tasks) {
        const parentTask = tasks.find(t => String(t.taskId) === String(task.mainTaskId) && t.projectId === task.projectId);
        if (parentTask) {
            isAssignedToParent = String(parentTask.assignedTo) === String(userEmail) ||
            parentTask.assignedUsers?.some(u => String(u) === String(userEmail));
        }
    }

    // Check if task is in Migration Experts swimlane (for assignment dropdown member selection)
    let isParentInExpertsSwimlane = false;
    if (task.mainTaskId && tasks) {
        // Subtask - check parent task's swimlane
        const parentTask = tasks.find(t => String(t.taskId) === String(task.mainTaskId) && t.projectId === task.projectId);
        if (parentTask) {
            isParentInExpertsSwimlane = parentTask.swimlaneId !== teamSwimlaneId &&
            parentTask.swimlaneId !== null;
        }
    } else if (task.swimlaneId) {
        // Main task - check its own swimlane
        isParentInExpertsSwimlane = task.swimlaneId !== teamSwimlaneId;
    }

    const canManageAsParentAssignee = isAssignedToParent && !isParentInExpertsSwimlane;
    // Admin portal can edit ALL tasks; client portal follows swimlane rules
    // Closed subtasks are fully locked for clients - only admins can modify or reopen
    const isLockedClosedSubtask = !isAdminProp && !!task?.mainTaskId && task?.isClosed === true;
    const canEditTask = !readOnly && !isLockedClosedSubtask && (isAdminProp || (((isTeamTask && (isClientAdmin || isAssignedToUser)) || canManageAsParentAssignee) && !isParentInExpertsSwimlane));
    const canCreateSubtask = !readOnly && (isAdminProp || (isTeamTask && (isClientAdmin || isAssignedToUser)));

    // Check if this task is blocked (only subtasks can be blocked)
    const taskBlockers = task?.mainTaskId ? getBlockers(blockedTasksMap, task?.taskId, task?.projectId, isTemplateMode) : [];
    const isTaskBlocked = taskBlockers.length > 0 && !task?.isClosed;

    /**
     * Returns true when the save landed, false when it did not (Bill, 2026-08-03).
     * Callers that must keep the user's input on failure - the link editor - need a
     * signal, and this catches internally so they cannot see the error otherwise.
     */
    const handleSaveChanges = async (fieldUpdates = null, field = null) => {
        const dataToSave = { version: task.version, ...(fieldUpdates || editedTaskData) };
        console.log('TaskDetailsModal handleSaveChanges called with:', dataToSave, 'field:', field);
        try {
            await onUpdate(task.taskId, dataToSave, field, task.projectId);
            return true;
        } catch (err) {
            if (err.message && err.message.includes('Version conflict')) {
                alert('This task has been updated by another user. Please close and reopen to get the latest version.');
            } else {
                alert(`Failed to save ${FIELD_LABEL[field] || 'changes'}. Please try again.`);
            }
            return false;
        }
    };

    /**
     * Start and end save as ONE write (Bill, 2026-08-03). A date range is one
     * value: saving the two halves separately made the result depend on which
     * field you edited first, because each was validated against the other's
     * stored value.
     *
     * Only fields that actually changed are sent, so an untouched date is never
     * re-clamped and never triggers a dialog the user did not ask for.
     */
    const handleDatePairBlur = (e) => {
        // Tabbing to the other date field must not save - the pair saves once,
        // when focus leaves both of them.
        if (e?.relatedTarget?.dataset?.datePair === 'task-dates') return;

        const storedStart = formatDateForInput(task.startDate) || null;
        const storedDue = formatDateForInput(task.dueDate) || null;
        const nextStart = editedTaskData?.start !== undefined
            ? (editedTaskData.start || null) : storedStart;
        const nextDue = editedTaskData?.due_date !== undefined
            ? (editedTaskData.due_date || null) : storedDue;

        const payload = {};
        if (nextStart !== storedStart) payload.start = nextStart;
        if (nextDue !== storedDue) payload.due_date = nextDue;
        if (Object.keys(payload).length === 0) return;

        handleSaveChanges(payload, 'dates');
    };

    const handleAddComment = async () => {
        if (!newComment.trim()) return;
        await onCommentAdd(task.taskId, newComment);
        setNewComment('');
        setCommentFormClosing(true);
        setTimeout(() => {
            setShowCommentForm(false);
            setCommentFormClosing(false);
        }, 300);
    };

    const handleEditComment = async (commentId) => {
        if (!editedCommentText.trim()) return;
        await onCommentEdit(task.taskId, commentId, editedCommentText);
        setEditingCommentId(null);
        setEditedCommentText('');
    };

    const handleCreateSubtask = async () => {
        if (isTemplateMode) {
            if (!newSubtask.subject.trim() || !newSubtask.relativeStart || !newSubtask.relativeEnd) return;
        } else {
            if (!newSubtask.subject.trim() || !newSubtask.start || !newSubtask.dueDate) return;
        }
        await onSubtaskCreate(task.taskId, newSubtask, task.projectId);
        if (isTemplateMode) {
            setNewSubtask({ subject: '', description: '', assignedUsers: [], relativeStart: 1, relativeEnd: 5, isMeeting: false, meetingType: '' });
        } else {
            const today = new Date().toISOString().split('T')[0];
            setNewSubtask({ subject: '', description: '', assignedUsers: [], start: today, dueDate: today, isMeeting: false, meetingType: '' });
        }
        setShowSubtaskForm(false);
    };

    // Handle closure comment confirmation
    const handleClosureConfirm = async (comment) => {
        if (!closurePending) return;
        setIsSubmittingClosure(true);

        try {
            // Add the closure comment first
            await onCommentAdd(task.taskId, comment, true);
            // Then save the status change
            const updatedData = {...editedTaskData, status: closurePending.statusId};
            setEditedTaskData(updatedData);
            await handleSaveChanges(updatedData, 'status');
        } catch (err) {
            console.error('Closure failed:', err);
            alert('Failed to close task. Please try again.');
            setIsSubmittingClosure(false);
            return;
        }

        // Success - close closure modal and navigate
        setShowClosureModal(false);
        setClosurePending(null);
        setIsSubmittingClosure(false);
        // Closing the LAST open subtask closes the parent main task too
        // (syncMainTaskClosedState does that server side), so returning to the
        // parent would land on a modal for a task that is now finished. Dismiss
        // the lot back to the board instead. Siblings only - this subtask has
        // just been closed and the local `tasks` copy may not show it yet.
        const siblingsAllClosed = task.mainTaskId && !tasks?.some(t =>
            String(t.mainTaskId) === String(task.mainTaskId) &&
            t.projectId === task.projectId &&
            String(t.taskId) !== String(task.taskId) &&
            t.isClosed !== true
        );
        if (task.mainTaskId && onSubtaskClick && !siblingsAllClosed) {
            onSubtaskClick(task.mainTaskId);
        } else {
            onClose();
        }
    };

    const handleClosureCancel = () => {
        setShowClosureModal(false);
        setClosurePending(null);
    };

    // Handle saving link changes
    const handleSaveLink = async (linkNumber, url, description) => {
        setSavingLinks(true);
        try {
            const currentLinks = Array.isArray(task.links) ? [...task.links] : [];
            const linkIndex = linkNumber - 1;
            if (url || description) {
                if (linkIndex < currentLinks.length) {
                    currentLinks[linkIndex] = { url, description };
                } else {
                    currentLinks.push({ url, description });
                }
            } else if (linkIndex < currentLinks.length) {
                currentLinks.splice(linkIndex, 1);
            }
            // Through handleSaveChanges like every other field, so the payload
            // carries the version. Calling onUpdate directly omitted it, and the
            // client API rejects a versionless update (Bill, 2026-08-03).
            const saved = await handleSaveChanges({ links: currentLinks }, 'links');
            // Only close the editor when it actually saved - otherwise the user
            // loses what they typed, with nothing to show for it.
            if (saved) {
                setEditingLinkNumber(null);
                setLinkEditData({ url: '', description: '' });
            }
        } finally {
            setSavingLinks(false);
        }
    };

    // Get the next available link number for adding a new link
    const getNextLinkNumber = () => {
        const pairs = extractLinkPairs(task);
        if (pairs.length === 0) return 1;
        return Math.max(...pairs.map(p => p.number)) + 1;
    };

    // Check if links can be added (DynamoDB uses links array, no slot limit)
    const hasAvailableLinkSlots = () => {
        return true;
    };

    // Script section handlers (template mode only)
    const handleViewScript = (script) => {
        // Calculate base path from current URL (handles preview paths like /gantt/)
        const pathname = window.location.pathname;
        const adminIndex = pathname.indexOf('/admin/');
        const basePath = adminIndex > 0 ? pathname.substring(0, adminIndex) : '';
        window.open(`${basePath}/admin/scripts/${script.scriptId}`, '_blank');
    };

    // TME Wiki section handlers (TWK-23, §9.4)
    const wikiPageHref = (link) => {
        // The same base-path computation the script link uses: the router is
        // mounted under PUBLIC_URL, which on preview is `/gantt`, so an absolute
        // `/admin/...` would leave the SPA.
        const pathname = window.location.pathname;
        const adminIndex = pathname.indexOf('/admin/');
        const basePath = adminIndex > 0 ? pathname.substring(0, adminIndex) : '';
        return `${basePath}/admin/tme-wiki/wikis/${link.wikiId}/pages/${link.pageId}`;
    };

    const handleViewWikiPage = (link) => {
        window.open(wikiPageHref(link), '_blank');
    };

    const handleOpenLinkWikiModal = async () => {
        setShowLinkWikiModal(true);
        setLoadingAvailableWikiPages(true);
        try {
            const base = `${process.env.REACT_APP_API_URL}/admin/tme-wiki`;
            const wikisResponse = await apiFetch(`${base}/wikis`);
            if (!wikisResponse.ok) {
                setAvailableWikiPages([]);
                return;
            }
            const { wikis = [] } = await wikisResponse.json();
            // One listing per wiki, on demand and only while the picker is open.
            // The listing is PROJECTED - it carries no bodies (§6.7).
            const perWiki = await Promise.all(wikis.map(async (wiki) => {
                const response = await apiFetch(`${base}/wikis/${wiki.wikiId}/pages`);
                if (!response.ok) return [];
                const { pages = [] } = await response.json();
                return pages
                    .filter(page => !page.archivedAt)
                    .map(page => ({ ...page, wikiName: wiki.name }));
            }));
            setAvailableWikiPages(perWiki.flat());
        } catch (err) {
            console.error('Error loading TME wiki pages:', err);
            setAvailableWikiPages([]);
        } finally {
            setLoadingAvailableWikiPages(false);
        }
    };

    const handleLinkWikiPage = async (page) => {
        const taskName = task?.subject?.trim();
        if (!taskName) return;
        setLinkingWikiPage(page.pageId);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/tme-wiki/wikis/${page.wikiId}/pages/${page.pageId}/links`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ kind: 'task', targetId: taskName, targetLabel: taskName })
                }
            );
            if (!response.ok) {
                const data = await response.json();
                window.alert(data.message || 'Could not link that page');
                return;
            }
            setShowLinkWikiModal(false);
            await fetchTaskWikiPages();
        } finally {
            setLinkingWikiPage(null);
        }
    };

    const handleUnlinkWikiPage = async (link) => {
        setUnlinkingWikiPage(link.pageId);
        try {
            // ⚠️ THE TARGET IS ENCODED. A task target is a NAME, which may carry
            // a slash or an ampersand, and the route matches one path segment.
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/tme-wiki/wikis/${link.wikiId}/pages/${link.pageId}`
                + `/links/task/${encodeURIComponent(link.targetId)}`,
                { method: 'DELETE' }
            );
            if (!response.ok) {
                const data = await response.json();
                window.alert(data.message || 'Could not unlink that page');
                return;
            }
            await fetchTaskWikiPages();
        } finally {
            setUnlinkingWikiPage(null);
        }
    };

    const handleCopyScript = async (script) => {
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/scripts/${script.scriptId}/content`);
            if (response.ok) {
                const data = await response.json();
                await navigator.clipboard.writeText(data.content);
                setCopyFeedback(script.scriptId);
                setTimeout(() => setCopyFeedback(null), 2000);
            } else {
                console.error('Failed to fetch script content');
            }
        } catch (err) {
            console.error('Error copying script:', err);
        }
    };

    const handleDownloadScript = async (script) => {
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/scripts/${script.scriptId}/content`);
            if (response.ok) {
                const data = await response.json();
                const ext = script.scriptType === 'powershell' ? '.ps1' : '.sh';
                const filename = `${script.name}${ext}`;
                const blob = new Blob([data.content], { type: 'text/plain' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            } else {
                console.error('Failed to fetch script content for download');
            }
        } catch (err) {
            console.error('Error downloading script:', err);
        }
    };

    const handleUnlinkScript = async (script) => {
        if (!task?.subject) return;

        const taskName = task.subject.trim();
        setUnlinkingScript(script.scriptId);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/scripts/${script.scriptId}/links/${encodeURIComponent(taskName)}`,
                { method: 'DELETE' }
            );
            if (response.ok) {
                await fetchTaskScripts();
            } else {
                console.error('Failed to unlink script');
            }
        } catch (err) {
            console.error('Error unlinking script:', err);
        } finally {
            setUnlinkingScript(null);
        }
    };

    const handleOpenLinkScriptModal = async () => {
        setShowLinkScriptModal(true);
        setLoadingAvailableScripts(true);
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/scripts`);
            if (response.ok) {
                const data = await response.json();
                // Filter out already linked scripts
                const linkedIds = new Set(taskScripts.map(s => s.scriptId));
                const available = (data.scripts || []).filter(s => !linkedIds.has(s.scriptId));
                setAvailableScripts(available);
            } else {
                console.error('Failed to fetch available scripts');
                setAvailableScripts([]);
            }
        } catch (err) {
            console.error('Error fetching available scripts:', err);
            setAvailableScripts([]);
        } finally {
            setLoadingAvailableScripts(false);
        }
    };

    const handleLinkScript = async (scriptId) => {
        if (!task?.subject) return;

        const taskName = task.subject.trim();
        setLinkingScript(scriptId);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/scripts/${scriptId}/links`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ taskName })
                }
            );
            if (response.ok) {
                await fetchTaskScripts();
                // Remove from available scripts
                setAvailableScripts(prev => prev.filter(s => s.scriptId !== scriptId));
            } else {
                console.error('Failed to link script');
            }
        } catch (err) {
            console.error('Error linking script:', err);
        } finally {
            setLinkingScript(null);
        }
    };

    // Get current dependencies
    const currentDeps = parseDependencies(task);


    // Get task name by ID (handles both numeric and string ST# IDs)
    const getTaskName = (taskId) => {
        const foundTask = tasks.find(t => {
            // Must be from same project
            if (t.projectId !== task.projectId) return false;
            if (t.taskId === taskId) return true;
            // DynamoDB mode: t.taskId is "ST#0001", taskId might be numeric (1)
            if (typeof t.taskId === 'string' && t.taskId.startsWith('ST#')) {
                const numericRef = parseInt(t.taskId.replace('ST#', ''), 10);
                return numericRef === Number(taskId);
            }
            return false;
        });
        return foundTask?.subject || `Task #${taskId}`;
    };

    const subtasks = tasks
        .filter(t => String(t.mainTaskId) === String(task.taskId) && t.projectId === task.projectId)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

    return (
        <>
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-[60]">
        <div
            className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
            style={
                task._isAddin && task._isTeamSwimlane && task._addinColour
                    ? { borderLeft: '6px solid #3b82f6', borderRight: `6px solid ${task._addinColour}` }
                    : task._isAddin && task._addinColour
                    ? { borderLeft: `6px solid ${task._addinColour}` }
                    : isTeamTask
                    ? { borderLeft: '6px solid #3b82f6' }
                    : {}
            }
        >
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b p-6 z-10">
        <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
        {/* Visual indicator for Main Task vs Sub Task */}
        {task.mainTaskId ? (
            <div className="mb-2">
            <span className="inline-flex items-center gap-1.5 bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-xs font-medium">
            <FaTasks className="w-3 h-3" />
            Sub Task
            </span>
            </div>
        ) : (
            <div className="mb-2">
            <span className="inline-flex items-center gap-1.5 bg-blue-100 text-primary-700 px-3 py-1 rounded-full text-xs font-medium">
            <FaClipboardList className="w-3 h-3" />
            Main Task
            </span>
            </div>
        )}

        {/* Task Title */}
        {canEditTask ? (
            <div className="space-y-3">
            <input
            type="text"
            value={editedTaskData?.subject || ''}
            onChange={(e) => setEditedTaskData({...editedTaskData, subject: e.target.value})}
            onBlur={() => handleSaveChanges(null, 'subject')}
            className="text-2xl font-semibold text-gray-900 w-full border-b-2 border-gray-300 focus:border-primary-600 focus:outline-none pb-1"
            />
            </div>
        ) : (
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            {task.subject}
            </h2>
        )}

        <div className="flex items-center gap-3 text-sm text-gray-600 mt-2">
        <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">
        #{task.taskId}
        </span>

        {/* Add-in indicator */}
        {task._isAddin && task._addinColour && (
            <span
                className="px-2 py-1 rounded text-xs font-bold"
                style={{
                    backgroundColor: `${task._addinColour}20`,
                    color: task._addinColour
                }}
            >
                {task._addinAbbreviation || 'Add-in'}
            </span>
        )}

        {/* Status Dropdown or Display */}
        {canEditTask ? (
            isTaskBlocked ? (
                <div className="flex items-center gap-2">
                    <span
                        className="bg-red-100 text-red-700 px-3 py-1 rounded"
                        onMouseEnter={(e) => showBlockedHover(e, taskBlockers)}
                        onMouseLeave={hideBlockedHover}
                        onClick={() => {
                            const blockerList = taskBlockers.map(b =>
                                b.type === 'issue'
                                    ? `Issue ${b.id}: ${b.title}`
                                    : b.parentTitle ? `${b.parentTitle} / ${b.title}` : b.title
                            ).join('\n• ');
                            alert(`Cannot change status. Blocked by:\n• ${blockerList}`);
                        }}
                    >
                        Blocked
                    </span>
                    <span className="text-gray-500 text-sm">
                        Blocked by: {taskBlockers.map(b => b.type === 'issue' ? `Issue ${b.id}` : (b.parentTitle ? `${b.parentTitle} / ${b.title}` : b.title)).join(', ')}
                    </span>
                </div>
            ) : (
                <div className="flex items-center gap-2">
                <select
                value={editedTaskData?.status || ''}
                disabled={savingField === 'status'}
                onChange={async (e) => {
                    const newStatus = parseInt(e.target.value);
                    const isSubtask = !!task.mainTaskId;
                    const statusList = isSubtask ? taskStatuses : columns;
                    const selectedStatus = statusList.find(s => (isSubtask ? s.id : s.epicId) === newStatus);

                    // Check if selecting a closed status and task isn't already closed (subtask only)
                    if (selectedStatus?.isClosed && !task.isClosed && task.mainTaskId) {
                        setClosurePending({ statusId: newStatus });
                        setShowClosureModal(true);
                        // Reset dropdown to current value (will update after closure)
                        e.target.value = editedTaskData?.status || '';
                        return;
                    }

                    const updatedData = {...editedTaskData, status: newStatus};
                    setEditedTaskData(updatedData);
                    await handleSaveChanges(updatedData, 'status');
                    // Check if the new status is a closed status and close the modal
                    if (selectedStatus?.isClosed) {
                        onClose();
                    }
                }}
                className="bg-gray-100 text-gray-700 px-3 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                >
                {(task.mainTaskId ? taskStatuses : columns).map(item => {
                    const itemId = task.mainTaskId ? item.id : item.epicId;
                    return (
                        <option key={itemId} value={itemId}>
                            {item.name}
                        </option>
                    );
                })}
                </select>
                {savingField === 'status' && (
                    <svg className="animate-spin h-4 w-4 text-primary-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                )}
                </div>
            )
        ) : (
            task.statusName && (
                <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded">
                {task.statusName}
                </span>
            )
        )}
        </div>
        </div>
        <button
        onClick={onClose}
        disabled={isOperationInProgress}
        className="text-gray-400 hover:text-gray-600 ml-4 disabled:opacity-50 disabled:cursor-not-allowed"
        title={isOperationInProgress ? 'Please wait for operation to complete' : 'Close'}
        >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
        </button>
        </div>

        {/* Back to Main Task button */}
        {task.mainTaskId && (
            <div className="pb-4">
            <button
            onClick={() => {
                // Wait a moment if changes were just made
                setTimeout(() => {
                    onSubtaskClick(task.mainTaskId);
                }, 300);
            }}
            className="flex items-center gap-2 text-primary-600 hover:text-primary-800 text-sm font-medium"
            >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to {tasks?.find(t => t.taskId === task.mainTaskId && t.projectId === task.projectId)?.subject || 'Main Task'}
            </button>
            </div>
        )}
        </div>

        {/* Modal Body - Two Column Layout */}
        <div className="p-6 flex gap-6">
        {/* Left Column - Description, Assignees, Metadata, Tags, Comments */}
        <div className="flex-1 border-r pr-6 space-y-4 max-h-[calc(90vh-200px)] overflow-y-auto">
        {/* Description */}
        <div>
        <div className="flex justify-between items-center mb-2">
        <h3 className="text-sm font-medium text-gray-700">Description</h3>
        {canEditTask && !editingDescription && (
            <button
            onClick={() => setEditingDescription(true)}
            className="text-blue-500 hover:text-blue-700 p-1"
            title="Edit description"
            >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            </button>
        )}
        </div>
        {canEditTask && editingDescription ? (
            <div className={`space-y-2 ${descriptionEditorClosing ? 'animate-slideUp' : 'animate-slideDown'}`}>
            <textarea
            value={editedTaskData?.description || ''}
            onChange={(e) => setEditedTaskData({...editedTaskData, description: e.target.value})}
            rows="6"
            className="w-full bg-gray-50 rounded p-4 text-gray-700 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            placeholder="Add a description..."
            />
            <div className="flex gap-2">
            <button
            onClick={async () => {
                await handleSaveChanges(null, 'description');
                setEditingDescription(false);
            }}
            disabled={savingField === 'description'}
            className="px-3 py-1 bg-primary-600 text-white text-sm rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
            {savingField === 'description' ? (
                <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
                </>
            ) : 'Save'}
            </button>
            <button
            onClick={() => {
                setDescriptionEditorClosing(true);
                setTimeout(() => {
                    setEditedTaskData({...editedTaskData, description: task.description});
                    setEditingDescription(false);
                    setDescriptionEditorClosing(false);
                }, 300);
            }}
            disabled={savingField === 'description'}
            className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 disabled:opacity-50"
            >
            Cancel
            </button>
            </div>
            </div>
        ) : (
            task.description ? (
                <div className="bg-gray-50 rounded p-4 text-gray-700 whitespace-pre-wrap border border-gray-200">
                {task.description}
                </div>
            ) : (
                <div className="bg-gray-50 rounded p-4 text-gray-400 italic border border-gray-200">
                No description
                </div>
            )
        )}
        </div>

        {/* Assignees */}
        <div>
        <div className="flex justify-between items-center mb-2">
        <h3 className="text-sm font-medium text-gray-700">Assigned To</h3>
        {(isAdminProp || (isClientAdmin && isTeamTask)) && !readOnly && (
            <button
            onClick={async () => {
                await onOpenAssignmentEditor();
                setShowAssignmentEditor(!showAssignmentEditor);
            }}
            className="text-primary-600 hover:text-primary-800 text-sm font-medium flex items-center gap-1"
            >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit
            </button>
        )}
        </div>

        {showAssignmentEditor && (isAdminProp || (isClientAdmin && isTeamTask)) && !readOnly && (isParentInExpertsSwimlane ? adminTeamMembers?.length > 0 : projectMembers?.length > 0) && (
            <div className={`mb-4 p-3 bg-blue-50 border border-blue-200 rounded space-y-2 ${assignmentEditorClosing ? 'animate-slideUp' : 'animate-slideDown'}`}>
            {/* For subtasks, show option to unassign */}
            {task.mainTaskId && (
                <label className="flex items-center gap-3 p-2 hover:bg-blue-100 rounded cursor-pointer">
                <input
                type="radio"
                name="subtask-assignee"
                checked={!task.assignedTo}
                onChange={() => {
                    task.assignedTo = null;
                    setEditedTaskData({...editedTaskData});
                }}
                className="w-4 h-4 text-primary-600 focus:ring-2 focus:ring-primary-500"
                />
                <div className="flex-1">
                <p className="text-sm font-medium text-gray-500 italic">Unassigned</p>
                </div>
                </label>
            )}
            {(() => {
                const membersForAssignment = isParentInExpertsSwimlane
                    ? adminTeamMembers
                    : projectMembers;
                return membersForAssignment.map(pm => {
                // Use pm directly - both sources provide email and name
                const member = pm;
                if (!member) return null;
                const currentAssignees = editedTaskData?.assignedUsers || task.assignedUsers || [];
                const isAssigned = currentAssignees.some(u => String(u) === String(member.email)) || String(task.assignedTo) === String(member.email);
                return (
                    <label
                    key={member.email}
                    className={`flex items-center gap-3 p-2 rounded ${member.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-100 cursor-pointer'}`}
                    title={member.disabled ? 'User is disabled' : undefined}
                    >
                    {/* Use radio for subtasks (single assignee), checkbox for main tasks (multiple) */}
                    {task.mainTaskId ? (
                        <input
                        type="radio"
                        name="subtask-assignee"
                        disabled={member.disabled === true}
                        checked={String(task.assignedTo) === String(member.email)}
                        onChange={() => {
                            task.assignedTo = member.email;
                            task.assignedToName = member.name || member.email;
                            setEditedTaskData({...editedTaskData});
                        }}
                        className="w-4 h-4 text-primary-600 focus:ring-2 focus:ring-primary-500"
                        />
                    ) : (
                        <input
                        type="checkbox"
                        checked={isAssigned}
                        disabled={member.disabled === true}
                        onChange={(e) => {
                            const currentAssignees = editedTaskData?.assignedUsers || task.assignedUsers || (task.assignedTo ? [task.assignedTo] : []);
                            let newAssignees;

                            if (e.target.checked) {
                                newAssignees = [...currentAssignees, member.email];
                            } else {
                                newAssignees = currentAssignees.filter(id => String(id) !== String(member.email));
                            }

                            setEditedTaskData({...editedTaskData, assignedUsers: newAssignees});
                        }}
                        className="w-4 h-4 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                        />
                    )}
                    <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                    {member.name || member.email}
                    </p>
                    <p className="text-xs text-gray-500">{member.email}</p>
                    </div>
                    </label>
                );
            })})()}
            <button
            onClick={async () => {
                // Save assignments
                if (task.mainTaskId) {
                    await handleSaveChanges({ assigned_to: task.assignedTo, assignedToName: task.assignedToName }, 'assignment');
                } else {
                    // Build names array for display (emails are the unique identifiers)
                    const membersList = isParentInExpertsSwimlane ? adminTeamMembers : projectMembers;
                    const usersToSave = editedTaskData?.assignedUsers || task.assignedUsers || [];
                    const assignedUsersNames = usersToSave.map(userEmail => {
                        const m = membersList.find(mem => String(mem.email) === String(userEmail));
                        return m?.name || m?.email || userEmail;
                    });
                    // No identity, no label: when the list is emptied the name goes
                    // with it. The old `|| task.assignedToName` fallback kept a stale
                    // name behind, which is what made an unassigned main task look
                    // assigned on the Kanban card while staying locked (Bill, 2026-08-03).
                    await handleSaveChanges({
                        assignedUsers: usersToSave,
                        assignedUsersNames,
                        assignedToName: assignedUsersNames[0] || null
                    }, 'assignment');
                }

                setAssignmentEditorClosing(true);
                setTimeout(() => {
                    setShowAssignmentEditor(false);
                    setAssignmentEditorClosing(false);
                }, 300);
            }}
            disabled={savingField === 'assignment'}
            className="mt-2 w-full px-3 py-2 bg-primary-600 text-white text-sm rounded hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
            {savingField === 'assignment' ? (
                <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
                </>
            ) : 'Done'}
            </button>
            </div>
        )}

        {/* Display Current Assignees */}
        <div className="space-y-3">
        {/* Main tasks: show assignedUsersNames array (multiple assignees) */}
        {!task.mainTaskId && task.assignedUsersNames && task.assignedUsersNames.length > 0 ? (
            task.assignedUsersNames.map((name, idx) => (
                <div key={idx} className="flex items-center gap-3">
                <p className="font-medium text-gray-900">{name}</p>
                </div>
            ))
        ) : /* Subtasks: show assignedToName (single assignee) */
        task.mainTaskId && task.assignedToName ? (
            <div className="flex items-center gap-3">
            <p className="font-medium text-gray-900">{task.assignedToName}</p>
            </div>
        ) : (
            <p className="text-sm text-gray-500 italic">No one assigned</p>
        )}
        </div>
        </div>

        {/* Task Links Section */}
        <div>
        <button
        onClick={() => setLinksExpanded(!linksExpanded)}
        className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 w-full"
        >
        {linksExpanded ? (
            <FaChevronDown className="w-3 h-3" />
        ) : (
            <FaChevronRight className="w-3 h-3" />
        )}
        <FaLink className="w-4 h-4" />
        <span>Task Links ({extractLinkPairs(task).length})</span>
        </button>

        {linksExpanded && (
            <div className="mt-3 space-y-2">
            {extractLinkPairs(task).length === 0 ? (
                <p className="text-sm text-gray-500 italic pl-5">No links added</p>
            ) : (
                extractLinkPairs(task).map((pair) => (
                    <div
                    key={pair.number}
                    className="flex items-start gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg"
                    >
                    <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                    {pair.description || 'No description'}
                    </p>
                    {pair.url ? (
                        <a
                        href={pair.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary-600 hover:text-primary-800 hover:underline truncate block"
                        title={pair.url}
                        >
                        {pair.url}
                        </a>
                    ) : (
                        <p className="text-sm text-gray-400 italic">No URL</p>
                    )}
                    </div>
                    {canEditTask && (
                        <button
                        onClick={() => {
                            setEditingLinkNumber(pair.number);
                            setLinkEditData({ url: pair.url, description: pair.description });
                        }}
                        className="text-gray-400 hover:text-primary-600 p-1 flex-shrink-0"
                        title="Edit link"
                        >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        </button>
                    )}
                    </div>
                ))
            )}

            {/* Add Link Button */}
            {canEditTask && hasAvailableLinkSlots() && (
                <button
                onClick={() => {
                    const nextNum = getNextLinkNumber();
                    setEditingLinkNumber(nextNum);
                    setLinkEditData({ url: '', description: '' });
                }}
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800 pl-5"
                >
                <FaPlus className="w-3 h-3" />
                Add Link
                </button>
            )}
            </div>
        )}

        {/* Link Edit Popout */}
        {editingLinkNumber !== null && (
            <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {extractLinkPairs(task).find(p => p.number === editingLinkNumber)?.url ? 'Edit Link' : 'Add Link'}
            </h3>
            <div className="space-y-4">
            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
            </label>
            <input
            type="text"
            value={linkEditData.description}
            onChange={(e) => setLinkEditData({ ...linkEditData, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Enter link description"
            autoFocus
            />
            </div>
            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
            URL
            </label>
            <input
            type="url"
            value={linkEditData.url}
            onChange={(e) => setLinkEditData({ ...linkEditData, url: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="https://..."
            />
            </div>
            </div>
            <div className="flex justify-between mt-6">
            <div>
            {/* Delete button - only show when editing an existing link */}
            {extractLinkPairs(task).find(p => p.number === editingLinkNumber) && (
                <button
                onClick={() => handleSaveLink(editingLinkNumber, '', '')}
                disabled={savingLinks}
                className="px-4 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded disabled:opacity-50"
                >
                Delete
                </button>
            )}
            </div>
            <div className="flex gap-3">
            <button
            onClick={() => {
                setEditingLinkNumber(null);
                setLinkEditData({ url: '', description: '' });
            }}
            disabled={savingLinks}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50"
            >
            Cancel
            </button>
            <button
            onClick={() => handleSaveLink(editingLinkNumber, linkEditData.url, linkEditData.description)}
            disabled={savingLinks || (!linkEditData.url && !linkEditData.description)}
            className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            >
            {savingLinks ? (
                <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
                </>
            ) : 'Save'}
            </button>
            </div>
            </div>
            </div>
            </div>
        )}
        </div>

        {/* Scripts Section - show in template mode or with showScripts prop */}
        {(isTemplateMode || showScripts) && (
        <div>
        <button
            onClick={() => setScriptsExpanded(!scriptsExpanded)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 w-full"
        >
        {scriptsExpanded ? (
            <FaChevronDown className="w-3 h-3" />
        ) : (
            <FaChevronRight className="w-3 h-3" />
        )}
        <FaCode className="w-4 h-4" />
        <span>Scripts ({taskScripts.length})</span>
        {loadingScripts && (
            <svg className="animate-spin h-3 w-3 ml-1 text-gray-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
        )}
        </button>

        {scriptsExpanded && (
            <div className="mt-3 space-y-2">
            {taskScripts.length === 0 ? (
                <p className="text-sm text-gray-500 italic pl-5">No scripts linked</p>
            ) : (
                taskScripts.map((script) => (
                    <div
                    key={script.scriptId}
                    className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded-lg"
                    >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                            script.scriptType === 'powershell' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                        }`}>
                            {script.scriptType === 'powershell' ? 'PS1' : 'SH'}
                        </span>
                        <span className="text-sm text-gray-800 truncate">{script.name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => handleViewScript(script)}
                            className="p-1 text-gray-500 hover:text-primary-600"
                            title="View in Script Repository"
                        >
                            <FaExternalLinkAlt className="w-3 h-3" />
                        </button>
                        <button
                            onClick={() => handleCopyScript(script)}
                            className={`p-1 ${copyFeedback === script.scriptId ? 'text-green-600' : 'text-gray-500 hover:text-primary-600'}`}
                            title={copyFeedback === script.scriptId ? 'Copied!' : 'Copy to clipboard'}
                        >
                            <FaCopy className="w-3 h-3" />
                        </button>
                        <button
                            onClick={() => handleDownloadScript(script)}
                            className="p-1 text-gray-500 hover:text-primary-600"
                            title="Download"
                        >
                            <FaDownload className="w-3 h-3" />
                        </button>
                        {isTemplateMode && (
                        <button
                            onClick={() => handleUnlinkScript(script)}
                            disabled={unlinkingScript === script.scriptId}
                            className="p-1 text-gray-500 hover:text-red-600 disabled:opacity-50"
                            title="Unlink"
                        >
                            {unlinkingScript === script.scriptId ? (
                                <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                <FaTimes className="w-3 h-3" />
                            )}
                        </button>
                        )}
                    </div>
                    </div>
                ))
            )}
            {isTemplateMode && (
            <button
                onClick={handleOpenLinkScriptModal}
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800 pl-5"
            >
                <FaPlus className="w-3 h-3" />
                Link Script
            </button>
            )}
            </div>
        )}

        {/* Link Script Modal */}
        {showLinkScriptModal && (
            <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-[70]">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[80vh] flex flex-col">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Link Script to Task</h3>
            {loadingAvailableScripts ? (
                <div className="flex items-center justify-center py-8">
                <svg className="animate-spin h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                </div>
            ) : availableScripts.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No available scripts to link</p>
            ) : (
                <div className="flex-1 overflow-y-auto space-y-2">
                {availableScripts.map((script) => (
                    <div
                    key={script.scriptId}
                    className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100"
                    >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                            script.scriptType === 'powershell' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                        }`}>
                            {script.scriptType === 'powershell' ? 'PS1' : 'SH'}
                        </span>
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-800 truncate">{script.name}</p>
                            {script.description && (
                                <p className="text-xs text-gray-500 truncate">{script.description}</p>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={() => handleLinkScript(script.scriptId)}
                        disabled={linkingScript === script.scriptId}
                        className="px-3 py-1 bg-primary-600 text-white text-sm rounded hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1"
                    >
                        {linkingScript === script.scriptId ? (
                            <>
                            <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Linking...
                            </>
                        ) : 'Link'}
                    </button>
                    </div>
                ))}
                </div>
            )}
            <div className="flex justify-end mt-4 pt-4 border-t">
            <button
                onClick={() => setShowLinkScriptModal(false)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
            >
                Close
            </button>
            </div>
            </div>
            </div>
        )}
        </div>
        )}

        {/* TME Wiki Section (TWK-23, §9.4) - the Scripts block's sibling.
            ⚠️ THE CLIENT PORTAL PASSES NEITHER PROP, so this never renders there
            and the fetch above never fires. Internal procedure text is admin
            only, and the isolation suite proves the absence rather than
            trusting it. */}
        {(isTemplateMode || showWikiLinks) && (
        <div>
        <button
            onClick={() => setWikiExpanded(!wikiExpanded)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 w-full"
        >
        {wikiExpanded ? (
            <FaChevronDown className="w-3 h-3" />
        ) : (
            <FaChevronRight className="w-3 h-3" />
        )}
        <FaBook className="w-4 h-4" />
        <span>Wiki ({taskWikiPages.length})</span>
        {loadingWikiPages && (
            <svg className="animate-spin h-3 w-3 ml-1 text-gray-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
        )}
        </button>

        {wikiExpanded && (
            <div className="mt-3 space-y-2">
            {taskWikiPages.length === 0 ? (
                <p className="text-sm text-gray-500 italic pl-5">No wiki pages linked</p>
            ) : (
                taskWikiPages.map((link) => (
                    <div
                    key={`${link.wikiId}#${link.pageId}`}
                    className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded-lg"
                    >
                    {/* ⚠️ THE PAGE TITLE COMES OFF THE LINK ROW (H1, R23). It is
                        mirrored there precisely so this list needs no second
                        read to name the page it is offering. */}
                    <span className="text-sm text-gray-800 truncate min-w-0 flex-1">{link.pageTitle}</span>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => handleViewWikiPage(link)}
                            className="p-1 text-gray-500 hover:text-primary-600"
                            title="Open in TME Wiki"
                        >
                            <FaExternalLinkAlt className="w-3 h-3" />
                        </button>
                        {isTemplateMode && (
                        <button
                            onClick={() => handleUnlinkWikiPage(link)}
                            disabled={unlinkingWikiPage === link.pageId}
                            className="p-1 text-gray-500 hover:text-red-600 disabled:opacity-50"
                            title="Unlink"
                        >
                            {unlinkingWikiPage === link.pageId ? (
                                <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                <FaTimes className="w-3 h-3" />
                            )}
                        </button>
                        )}
                    </div>
                    </div>
                ))
            )}
            {isTemplateMode && (
            <button
                onClick={handleOpenLinkWikiModal}
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800 pl-5"
            >
                <FaPlus className="w-3 h-3" />
                Link Wiki Page
            </button>
            )}
            </div>
        )}

        {/* Link Wiki Page Modal */}
        {showLinkWikiModal && (
            <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-[70]">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[80vh] flex flex-col">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Link a TME Wiki Page</h3>
            {loadingAvailableWikiPages ? (
                <div className="flex items-center justify-center py-8">
                <svg className="animate-spin h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                </div>
            ) : availableWikiPages.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No wiki pages to link</p>
            ) : (
                <div className="flex-1 overflow-y-auto space-y-2">
                {availableWikiPages.map((page) => (
                    <div
                    key={`${page.wikiId}#${page.pageId}`}
                    className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100"
                    >
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-800 truncate">{page.title}</p>
                        <p className="text-xs text-gray-500 truncate">{page.wikiName}</p>
                    </div>
                    <button
                        onClick={() => handleLinkWikiPage(page)}
                        disabled={linkingWikiPage === page.pageId}
                        className="px-3 py-1 bg-primary-600 text-white text-sm rounded hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1"
                    >
                        {linkingWikiPage === page.pageId ? (
                            <>
                            <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Linking...
                            </>
                        ) : 'Link'}
                    </button>
                    </div>
                ))}
                </div>
            )}
            <div className="flex justify-end mt-4 pt-4 border-t">
            <button
                onClick={() => setShowLinkWikiModal(false)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
            >
                Close
            </button>
            </div>
            </div>
            </div>
        )}
        </div>
        )}

        {/* Dependencies - only show for subtasks, not main tasks */}
        {task.mainTaskId && (
        <div>
        <button
            onClick={() => setDepsExpanded(!depsExpanded)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors"
        >
        {depsExpanded ? (
            <FaChevronDown className="w-3 h-3" />
        ) : (
            <FaChevronRight className="w-3 h-3" />
        )}
        <FaProjectDiagram className="w-4 h-4" />
        <span>Dependencies ({currentDeps.length})</span>
        </button>

        {depsExpanded && (
            <div className="mt-3 space-y-2">
            {/* Existing dependencies */}
            {currentDeps.length === 0 ? (
                <p className="text-sm text-gray-500 italic pl-5">No dependencies</p>
            ) : (
                currentDeps.map((dep) => (
                    <div
                    key={dep.taskId}
                    className="flex items-center justify-between gap-2 p-2 bg-gray-50 border border-gray-200 rounded-lg"
                    >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                            dep.type === 'SS' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                            {dep.type}
                        </span>
                        <span className="text-sm text-gray-800 truncate">{getTaskName(dep.taskId)}</span>
                    </div>
                    </div>
                ))
            )}

            </div>
        )}
        </div>
        )}

        {/* Dates Section - Different for main tasks vs subtasks */}
        {!task.mainTaskId ? (
            // Main task - show calculated date range from subtasks (read-only)
            (() => {
                const { start, end } = isTemplateMode
                    ? getEffectiveRelativeDateRange(task, tasks)
                    : getEffectiveDateRange(task, tasks);
                return (
                    <div>
                    <div className="flex justify-between items-center mb-2">
                    <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <FaCalendarAlt className="w-4 h-4" />
                    Task Dates
                    </h3>
                    </div>
                    <div className="flex items-center gap-4">
                    <div>
                    <span className="text-xs text-gray-500 block">Start</span>
                    {start != null ? (
                        <p className="font-medium text-gray-700">{isTemplateMode ? formatRelativeDay(start) : formatDateUK(start)}</p>
                    ) : (
                        <p className="text-gray-400 italic text-sm">Not set</p>
                    )}
                    </div>
                    <span className="text-gray-600 mt-4">{'\u2192'}</span>
                    <div>
                    <span className="text-xs text-gray-500 block">End</span>
                    {end != null ? (
                        isTemplateMode ? (
                        <p className="font-medium text-gray-700">{formatRelativeDay(end)}</p>
                        ) : (
                        <p className={`font-medium inline-flex items-center px-2 py-0.5 rounded ${getDueDateColour(end)}`}>
                        {formatDateUK(end)}
                        {isOverdue(end) && <span className="ml-2 text-xs">(Overdue)</span>}
                        {isWithin2Days(end) && !isOverdue(end) && <span className="ml-2 text-xs">(Due soon)</span>}
                        </p>
                        )
                    ) : (
                        <p className="text-gray-400 italic text-sm">Not set</p>
                    )}
                    </div>
                    </div>
                    </div>
                );
            })()
        ) : (
            // Subtask - show editable start and due dates
            <div>
            <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <FaCalendarAlt className="w-4 h-4" />
            Task Dates
            </h3>
            </div>
            {canEditTask ? (
                <>
                {isTemplateMode ? (
                <div className="flex items-center gap-4">
                {/* Relative Start Date */}
                <div>
                <span className="text-xs text-gray-500 block mb-1">Start</span>
                <div className="flex items-center gap-2">
                <RelativeDatePicker
                    value={editedTaskData?.relativeStart != null ? editedTaskData.relativeStart : task.relativeStart}
                    onChange={async (val) => {
                        setEditedTaskData({...editedTaskData, relativeStart: val});
                        await handleSaveChanges({ relativeStart: val }, 'relativeStart');
                    }}
                    disabled={savingField === 'relativeStart'}
                />
                {savingField === 'relativeStart' && (
                    <svg className="animate-spin h-4 w-4 text-primary-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                )}
                </div>
                </div>
                <span className="text-gray-600 mt-4">{'\u2192'}</span>
                {/* Relative End Date */}
                <div>
                <span className="text-xs text-gray-500 block mb-1">End</span>
                <div className="flex items-center gap-2">
                <RelativeDatePicker
                    value={editedTaskData?.relativeEnd != null ? editedTaskData.relativeEnd : task.relativeEnd}
                    onChange={async (val) => {
                        setEditedTaskData({...editedTaskData, relativeEnd: val});
                        await handleSaveChanges({ relativeEnd: val }, 'relativeEnd');
                    }}
                    disabled={savingField === 'relativeEnd'}
                />
                {savingField === 'relativeEnd' && (
                    <svg className="animate-spin h-4 w-4 text-primary-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                )}
                </div>
                </div>
                </div>
                ) : (
                <div className="flex items-center gap-4">
                {/* Start Date */}
                <div>
                <span className="text-xs text-gray-500 block mb-1">Start</span>
                <div className="flex items-center gap-2">
                <input
                type="date"
                value={formatDateForInput(editedTaskData?.start || task.startDate)}
                onChange={(e) => {
                    const newStart = e.target.value || null;
                    setEditedTaskData({...editedTaskData, start: newStart});
                }}
                onBlur={handleDatePairBlur}
                data-date-pair="task-dates"
                disabled={savingField === 'dates'}
                className="px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 text-sm"
                />
                {savingField === 'dates' && (
                    <svg className="animate-spin h-4 w-4 text-primary-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                )}
                </div>
                </div>
                <span className="text-gray-600 mt-4">{'\u2192'}</span>
                {/* Due Date */}
                <div>
                <span className="text-xs text-gray-500 block mb-1">End</span>
                <div className="flex items-center gap-2">
                <input
                type="date"
                value={formatDateForInput(editedTaskData?.due_date || task.dueDate)}
                onChange={(e) => {
                    const newDueDate = e.target.value || null;
                    setEditedTaskData({...editedTaskData, due_date: newDueDate});
                }}
                onBlur={handleDatePairBlur}
                data-date-pair="task-dates"
                disabled={savingField === 'dates'}
                className={`px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 text-sm ${getDueDateInputColour(task.dueDate)}`}
                />
                {savingField === 'dates' && (
                    <svg className="animate-spin h-4 w-4 text-primary-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                )}
                {isOverdue(task.dueDate) && <span className="text-xs text-red-600">(Overdue)</span>}
                </div>
                </div>
                </div>
                )}
                {/* Is Meeting - admin only */}
                {isAdminProp && (
                    <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={editedTaskData?.isMeeting ?? task.isMeeting ?? false}
                            onChange={async (e) => {
                            const newValue = e.target.checked;
                            setEditedTaskData({...editedTaskData, isMeeting: newValue});
                            await handleSaveChanges({ isMeeting: newValue }, 'isMeeting');
                            }}
                            disabled={savingField === 'isMeeting'}
                            className="w-4 h-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500 accent-gray-600"
                        />
                        <span className="text-sm font-medium text-gray-700">Mark as Meeting</span>
                        {savingField === 'isMeeting' && (
                            <svg className="animate-spin h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        )}
                        </label>
                        {(editedTaskData?.isMeeting ?? task.isMeeting) && (
                        <>
                        <div className="h-5 w-px bg-gray-300 mx-3"></div>
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700">Type:</label>
                            <input
                            type="text"
                            value={editedTaskData?.meetingType ?? task.meetingType ?? ''}
                            onChange={(e) => setEditedTaskData({...editedTaskData, meetingType: e.target.value})}
                            onBlur={async (e) => {
                                if ((editedTaskData?.meetingType ?? '') !== (task.meetingType ?? '')) {
                                await handleSaveChanges({ meetingType: e.target.value }, 'meetingType');
                                }
                            }}
                            disabled={savingField === 'meetingType'}
                            className="px-2 py-1 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-gray-500 w-48"
                            placeholder="e.g. Kickoff"
                            />
                            {savingField === 'meetingType' && (
                            <svg className="animate-spin h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            )}
                        </div>
                        </>
                        )}
                    </div>
                    </div>
                )}
                </>
            ) : isTemplateMode ? (
                <div className="flex items-center gap-4">
                <div>
                <span className="text-xs text-gray-500 block">Start</span>
                {task.relativeStart != null ? (
                    <p className="font-medium text-gray-700">{formatRelativeDay(task.relativeStart)}</p>
                ) : (
                    <p className="text-gray-400 italic text-sm">Not set</p>
                )}
                </div>
                <span className="text-gray-600 mt-4">{'\u2192'}</span>
                <div>
                <span className="text-xs text-gray-500 block">End</span>
                {task.relativeEnd != null ? (
                    <p className="font-medium text-gray-700">{formatRelativeDay(task.relativeEnd)}</p>
                ) : (
                    <p className="text-gray-400 italic text-sm">Not set</p>
                )}
                </div>
                {task.isMeeting && (
                    <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded">Meeting</span>
                )}
                </div>
            ) : (
                <div className="flex items-center gap-4">
                <div>
                <span className="text-xs text-gray-500 block">Start</span>
                {task.startDate ? (
                    <p className="font-medium text-gray-700">{formatDateUK(task.startDate)}</p>
                ) : (
                    <p className="text-gray-400 italic text-sm">Not set</p>
                )}
                </div>
                <span className="text-gray-600 mt-4">{'\u2192'}</span>
                <div>
                <span className="text-xs text-gray-500 block">End</span>
                {task.dueDate ? (
                    <p className={`font-medium inline-flex items-center px-2 py-0.5 rounded ${getDueDateColour(task.dueDate)}`}>
                    {formatDateUK(task.dueDate)}
                    {isOverdue(task.dueDate) && <span className="ml-2 text-xs">(Overdue)</span>}
                    {isWithin2Days(task.dueDate) && !isOverdue(task.dueDate) && <span className="ml-2 text-xs">(Due soon)</span>}
                    </p>
                ) : (
                    <p className="text-gray-400 italic text-sm">Not set</p>
                )}
                </div>
                {/* Meeting indicator for read-only view */}
                {task.isMeeting && (
                    <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded">Meeting</span>
                )}
                </div>
            )}
            </div>
        )}

        {/* Effort - MAIN TASKS ONLY, ADMIN ONLY (T469, D3/D4/D9).
            ⚠️ `!task.mainTaskId` is how this file identifies a main task
            everywhere else, so the same test is used here.
            ⚠️ `isAdminProp` and NOT `!readOnly`: readOnly is about editability,
            not about who is looking. Effort must never render in the client
            portal even if a value somehow reached it - the server strip (T466)
            is the guarantee, this is the second gate.
            ⛔ AND NOT ON THE CLIENT'S OWN LANE (D1, ECL-06). `isClientLaneTask`,
            NOT this file's own `isTeamTask` above: that one is the unguarded
            inline form, and on a lane-less project `undefined === undefined`
            makes it true for EVERY task - so the section would disappear on a
            project whose rows `coreMainTasks` is still counting, and two
            surfaces would disagree about the same task. One predicate for every
            effort gate. */}
        {isAdminProp && !task.mainTaskId
            && !isClientLaneTask(task, teamSwimlaneId, task.project) && (
            <div className="border-t pt-4">
            <div className="flex items-center gap-2 mb-2">
            <h3 className="text-sm font-medium text-gray-700">Effort</h3>
            <span
                className="text-xs text-gray-400"
                title="Work required, in days, separate from the dates. Quarter-day steps."
            >
                (days)
            </span>
            </div>
            <div className="flex items-center gap-4">
            <div>
            <span className="text-xs text-gray-500 block mb-1">Estimated</span>
            <EffortInput
                value={task.effortEstimated}
                saving={savingField === 'effortEstimated'}
                disabled={!canEditTask}
                onCommit={val => handleSaveChanges({ effortEstimated: val }, 'effortEstimated')}
            />
            </div>
            {/* D5: a template is never worked on, so it has no actual. */}
            {!isTemplateMode && (
                <div>
                <span className="text-xs text-gray-500 block mb-1">Actual</span>
                <EffortInput
                    value={task.effortActual}
                    saving={savingField === 'effortActual'}
                    disabled={!canEditTask}
                    onCommit={val => handleSaveChanges({ effortActual: val }, 'effortActual')}
                />
                </div>
            )}
            </div>
            </div>
        )}

        {/* Metadata Grid */}
        <div className="grid md:grid-cols-2 gap-4">
        <div>
        <h3 className="text-sm font-medium text-gray-700 mb-1">Created</h3>
        <p className="text-gray-600">
        {new Date(task.createdAt).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })}
        </p>
        </div>
        <div>
        <h3 className="text-sm font-medium text-gray-700 mb-1">Last Updated</h3>
        <p className="text-gray-600">
        {new Date(task.updatedAt).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })}
        </p>
        </div>
        </div>

        {/* Tags */}
        {task.tags && task.tags.length > 0 && (
            <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Tags</h3>
            <div className="flex flex-wrap gap-2">
            {task.tags.map((tag, idx) => (
                <span
                key={idx}
                className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                >
                {tag[0]}
                </span>
            ))}
            </div>
            </div>
        )}

        {/* Comments Section - Only show in left column for MAIN tasks */}
        {!task.mainTaskId && (
        <div className="sticky top-0">
        <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-medium text-gray-700">
        Comments ({task.comments?.filter(c => !c.deletedAt).length || 0})
        </h3>
        {task.comments?.some(c => c.deletedAt) && (
            <button
            onClick={() => setShowDeletedComments(!showDeletedComments)}
            className="text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1"
            >
            {showDeletedComments ? (
                <>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Hide Deleted
                </>
            ) : (
                <>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
                Show Deleted ({task.comments?.filter(c => c.deletedAt).length})
                </>
            )}
            </button>
        )}
        </div>

        {/* Existing Comments */}
        {task.comments && task.comments.filter(comment => showDeletedComments || !comment.deletedAt).length > 0 && (
            <div className="space-y-3 mb-4 max-h-[625px] overflow-y-auto">
            {task.comments.filter(comment => showDeletedComments || !comment.deletedAt).map((comment, idx) => (
                <div key={idx} className={`rounded-lg p-3 border ${comment.deletedAt ? 'bg-red-50 border-red-200 opacity-60' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                <span className={`font-medium text-sm ${comment.deletedAt ? 'text-red-700' : 'text-gray-900'}`}>{comment.authorName}</span>
                {comment.deletedAt && (
                    <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-medium">
                    Deleted
                    </span>
                )}
                {comment.isClosureComment && (
                    <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                    Closure comment
                    </span>
                )}
                <span className="text-xs text-gray-500">
                {new Date(comment.createdAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                })}
                </span>
                </div>
                {!readOnly && canEditTask && !comment.deletedAt && !comment.isClosureComment && (
                    <div className="flex items-center gap-1">
                    <button
                    onClick={() => {
                        setEditingCommentId(comment.commentId);
                        setEditedCommentText(comment.comment);
                        setTimeout(() => {
                            const textarea = document.querySelector('textarea');
                            if (textarea) {
                                textarea.parentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                        }, 50);
                    }}
                    className="text-blue-500 hover:text-blue-700 p-1"
                    title="Edit comment"
                    >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    </button>
                    <button
                    onClick={() => onCommentDelete(task.taskId, comment.commentId)}
                    disabled={deletingComment === comment.commentId}
                    className="text-red-500 hover:text-red-700 p-1 disabled:opacity-50"
                    title="Delete comment"
                    >
                    {deletingComment === comment.commentId ? (
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    )}
                    </button>
                    </div>
                )}
                </div>

                {/* Comment Text or Edit Form */}
                {editingCommentId === comment.commentId ? (
                    <div className="space-y-2">
                    <textarea
                    value={editedCommentText}
                    onChange={(e) => setEditedCommentText(e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none text-sm"
                    />
                    <div className="flex gap-2">
                    <button
                    onClick={() => handleEditComment(comment.commentId)}
                    disabled={editingComment === comment.commentId}
                    className="px-3 py-1 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1"
                    >
                    {editingComment === comment.commentId ? (
                        <>
                        <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                        </>
                    ) : 'Save'}
                    </button>
                    <button
                    onClick={() => {
                        setEditingCommentId(null);
                        setEditedCommentText('');
                    }}
                    disabled={editingComment === comment.commentId}
                    className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300 disabled:opacity-50"
                    >
                    Cancel
                    </button>
                    </div>
                    </div>
                ) : (
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.comment}</p>
                )}
                </div>
            ))}
            </div>
        )}

        {/* Add Comment */}
        {canEditTask && (
            <div className="sticky bottom-0 bg-white pt-2">
            <button
            onClick={() => setShowCommentForm(!showCommentForm)}
            className="text-primary-600 hover:text-primary-800 text-sm font-medium flex items-center gap-1 mb-2"
            >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Comment
            </button>

            {showCommentForm && (
                <div className={`space-y-2 mb-4 p-3 bg-blue-50 border border-blue-200 rounded ${commentFormClosing ? 'animate-slideUp' : 'animate-slideDown'}`}>
                <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none text-sm"
                placeholder="Add a comment..."
                />
                <div className="flex gap-2">
                <button
                onClick={handleAddComment}
                disabled={!newComment.trim() || addingComment}
                className="btn-primary text-sm flex-1"
                >
                {addingComment ? 'Adding...' : 'Add Comment'}
                </button>
                <button
                onClick={() => {
                    setCommentFormClosing(true);
                    setTimeout(() => {
                        setShowCommentForm(false);
                        setCommentFormClosing(false);
                        setNewComment('');
                    }, 300);
                }}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 text-sm"
                >
                Cancel
                </button>
                </div>
                </div>
            )}
            </div>
        )}
        </div>
        )}
        </div>

        {/* Right Column - Subtasks (main tasks) / Comments (subtasks) / Agenda (meetings) */}
        <div className={`flex-1 border-l pl-6 max-h-[calc(90vh-200px)] ${task.mainTaskId && task.isMeeting ? 'flex flex-col' : 'space-y-4 overflow-y-auto'}`}>
        {/* Subtasks Section - Only for main tasks */}
        {!task.mainTaskId && (subtasks.length > 0 || isTeamTask || isAdminProp) && (
            <div>
            <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-3">
            <h3 className="text-sm font-medium text-gray-700">
            Sub Tasks ({subtasks.length})
            </h3>
            {subtasks.filter(s => s.isClosed).length > 0 && (
                <button
                onClick={() => setHideCompletedSubtasks(!hideCompletedSubtasks)}
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                >
                {hideCompletedSubtasks ? (
                    <>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Show {subtasks.filter(s => s.isClosed).length} completed
                    </>
                ) : (
                    <>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                    Hide completed
                    </>
                )}
                </button>
            )}
            </div>
            {canCreateSubtask && (
                <button
                onClick={() => setShowSubtaskForm(!showSubtaskForm)}
                className="text-primary-600 hover:text-primary-800 text-sm font-medium flex items-center gap-1"
                >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Subtask
                </button>
            )}
            </div>

            {/* Add Subtask Form */}
            {showSubtaskForm && canCreateSubtask && (
                <div className={`mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3 ${subtaskFormClosing ? 'animate-slideUp' : 'animate-slideDown'}`}>
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                Subtask Title *
                </label>
                <input
                type="text"
                value={newSubtask.subject}
                onChange={(e) => setNewSubtask({...newSubtask, subject: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Enter subtask title"
                />
                </div>
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
                </label>
                <textarea
                value={newSubtask.description}
                onChange={(e) => setNewSubtask({...newSubtask, description: e.target.value})}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                placeholder="Enter description (optional)"
                />
                </div>
                {/* Date fields - mandatory */}
                {isTemplateMode ? (
                <div className="grid grid-cols-2 gap-4">
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                Start *
                </label>
                <RelativeDatePicker
                    value={newSubtask.relativeStart}
                    onChange={(val) => setNewSubtask({...newSubtask, relativeStart: val})}
                    className="w-full"
                />
                </div>
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                End *
                </label>
                <RelativeDatePicker
                    value={newSubtask.relativeEnd}
                    onChange={(val) => setNewSubtask({...newSubtask, relativeEnd: val})}
                    className="w-full"
                />
                </div>
                </div>
                ) : (
                <div className="grid grid-cols-2 gap-4">
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date *
                </label>
                <input
                type="date"
                value={newSubtask.start}
                onChange={(e) => setNewSubtask({...newSubtask, start: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                </div>
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date *
                </label>
                <input
                type="date"
                value={newSubtask.dueDate}
                onChange={(e) => setNewSubtask({...newSubtask, dueDate: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                </div>
                </div>
                )}
                {/* Subtask assignee - single select only */}
                {(isParentInExpertsSwimlane ? adminTeamMembers?.length > 0 : projectMembers?.length > 0) && (
                    <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assign Team Member
                    </label>
                    <div className="border border-gray-300 rounded p-3 max-h-48 overflow-y-auto space-y-2">
                    {/* Unassigned option */}
                    <label className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                    type="radio"
                    name="new-subtask-assignee"
                    checked={!newSubtask.assignedUsers || newSubtask.assignedUsers.length === 0}
                    onChange={() => setNewSubtask({...newSubtask, assignedUsers: []})}
                    disabled={creatingSubtask}
                    className="w-4 h-4 text-primary-600 focus:ring-2 focus:ring-primary-500"
                    />
                    <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500 italic">Unassigned</p>
                    </div>
                    </label>
                    {(() => {
                        const membersForAssignment = isParentInExpertsSwimlane
                            ? adminTeamMembers
                            : projectMembers;
                        return membersForAssignment.map(pm => {
                        // For admin team, use directly. For project members, lookup in members for full data.
                        const member = isParentInExpertsSwimlane
                            ? pm
                            : members.find(m => String(m.leadId) === String(pm.leadId));
                        if (!member) return null;

                        return (
                            <label
                            key={member.leadId}
                            className={`flex items-center gap-3 p-2 rounded ${member.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'}`}
                            title={member.disabled ? 'User is disabled' : undefined}
                            >
                            <input
                            type="radio"
                            name="new-subtask-assignee"
                            checked={newSubtask.assignedUsers?.some(u => String(u) === String(member.email)) || false}
                            onChange={() => setNewSubtask({...newSubtask, assignedUsers: [member.email], assignedToName: member.name || member.email})}
                            disabled={creatingSubtask || member.disabled === true}
                            className="w-4 h-4 text-primary-600 focus:ring-2 focus:ring-primary-500"
                            />
                            <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                            {member.name || member.email}
                            </p>
                            <p className="text-xs text-gray-500">{member.email}</p>
                            </div>
                            </label>
                        );
                    })})()}
                    </div>
                    </div>
                )}
                {/* Is Meeting checkbox and Meeting Type - admin only */}
                {isAdminProp && (
                    <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                    <input
                    type="checkbox"
                    checked={newSubtask.isMeeting || false}
                    onChange={(e) => setNewSubtask({...newSubtask, isMeeting: e.target.checked, meetingType: e.target.checked ? newSubtask.meetingType : ''})}
                    disabled={creatingSubtask}
                    className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                    />
                    <span className="text-sm text-gray-700">This is a meeting</span>
                    </label>
                    {newSubtask.isMeeting && (
                        <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700">Type:</span>
                        <input
                        type="text"
                        value={newSubtask.meetingType || ''}
                        onChange={(e) => setNewSubtask({...newSubtask, meetingType: e.target.value})}
                        disabled={creatingSubtask}
                        className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-yellow-500 w-32"
                        placeholder="e.g. Kickoff"
                        />
                        </div>
                    )}
                    </div>
                )}
                <div className="flex gap-2">
                <button
                onClick={handleCreateSubtask}
                disabled={!newSubtask.subject.trim() || (isTemplateMode ? (!newSubtask.relativeStart || !newSubtask.relativeEnd) : (!newSubtask.start || !newSubtask.dueDate)) || creatingSubtask}
                className="btn-primary text-sm flex items-center gap-2"
                >
                {creatingSubtask ? (
                    <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                    </>
                ) : (
                    'Create Subtask'
                )}
                </button>
                <button
                onClick={() => {
                    setSubtaskFormClosing(true);
                    setTimeout(() => {
                        setShowSubtaskForm(false);
                        setSubtaskFormClosing(false);
                        setNewSubtask({ subject: '', description: '', assignedUsers: [] });
                    }, 300);
                }}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 text-sm"
                >
                Cancel
                </button>
                </div>
                </div>
            )}

            {/* Existing Subtasks */}
            <div className="space-y-2 max-h-[625px] overflow-y-auto">
            {subtasks.filter(s => !hideCompletedSubtasks || !s.isClosed).map((subtask) => {
                // Get current user ID for assignment check (only in client portal, not admin)
                const currentUserId = !isAdminProp ? lead?.id : null;
                const currentUserEmail = !isAdminProp ? (lead?.email || '').toLowerCase() : null;
                const isAssignedToMe = !!subtask.assignedTo && ((currentUserId && String(subtask.assignedTo) === String(currentUserId)) || (currentUserEmail && String(subtask.assignedTo).toLowerCase() === currentUserEmail));
                const subtaskBlockers = getBlockers(blockedTasksMap, subtask.taskId, subtask.projectId, isTemplateMode);
                const subtaskIsBlocked = subtaskBlockers.length > 0;
                return (
                <div
                key={subtask.taskId}
                onClick={() => onSubtaskClick(subtask.taskId)}
                className={`flex items-center gap-3 p-3 rounded border cursor-pointer transition ${
                    subtask.isMeeting
                    ? 'border-yellow-300 hover:bg-yellow-100'
                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                }`}
                style={{
                    ...(subtask.isMeeting ? { backgroundColor: '#fef9e7' } : {}),
                    ...(subtaskIsBlocked ? { boxShadow: 'inset 4px 0 0 0 #dc2626' } :
                        subtask._isAddin && subtask._isTeamSwimlane && subtask._addinColour
                        ? { boxShadow: `inset 4px 0 0 0 #3b82f6, inset -4px 0 0 0 ${subtask._addinColour}` }
                        : subtask._isAddin && subtask._addinColour
                        ? { boxShadow: `inset 4px 0 0 0 ${subtask._addinColour}` }
                        : isTeamTask
                        ? { boxShadow: 'inset 4px 0 0 0 #3b82f6' }
                        : {})
                }}
                >
                <div className="flex-shrink-0">
                {subtask.isClosed ? (
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                ) : (
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                )}
                </div>
                <div className="flex-1">
                <p className={`text-gray-900 font-medium ${subtask.isClosed ? 'line-through' : ''}`}>
                {/* Assigned to me: highlight the title only, not the whole row */}
                <span className={isAssignedToMe ? 'inline-flex items-center gap-1.5 bg-orange-100 rounded px-1.5 py-0.5' : ''}>
                {/* Title on the wrapping span: react-icons renders a `title` prop as
                    a <title> child of the svg, which NativeTooltips cannot intercept. */}
                {isAssignedToMe && (
                    <span className="inline-flex flex-shrink-0" title="Assigned to you">
                        <FaUserCircle className="w-4 h-4 text-orange-400" />
                    </span>
                )}
                {subtask.subject}
                </span>
                {subtask.isMeeting && <span className="ml-2 text-yellow-700 font-normal text-xs">(Meeting)</span>}
                </p>
                {/* Line 1: Ref, Status, Start Date, End Date, Comments */}
                <div className="flex items-center gap-2 mt-1 text-xs text-gray-600">
                <span>#{subtask.taskId}</span>
                {subtask.statusName && (
                    <span
                    className="text-white font-semibold px-2 py-0.5 rounded"
                    style={{
                        backgroundColor: getStatusColour(subtask.statusName, subtask.isClosed, subtask.isMeeting),
                        textShadow: '0 1px 1px rgba(0, 0, 0, 0.9), 0 0 3px rgba(0, 0, 0, 0.55)',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.4)'
                    }}
                    >
                    {subtask.statusName}
                    </span>
                )}
                {subtaskIsBlocked && (
                    <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium bg-red-50 text-red-800 rounded"
                        onMouseEnter={(e) => showBlockedHover(e, subtaskBlockers)}
                        onMouseLeave={hideBlockedHover}
                    >
                        <FaExclamationTriangle className="text-red-600" size={10} />
                        Blocked
                    </span>
                )}
                {isTemplateMode ? (
                    <>
                    {subtask.relativeStart != null && (
                        <span className="flex items-center gap-1 text-gray-500">
                        <FaCalendarAlt className="w-3 h-3" />
                        {formatRelativeDay(subtask.relativeStart)}
                        </span>
                    )}
                    {subtask.relativeStart != null && subtask.relativeEnd != null && (
                        <span className="text-gray-600">{'\u2192'}</span>
                    )}
                    {subtask.relativeEnd != null && (
                        <span className="flex items-center gap-1 text-gray-500">
                        {subtask.relativeStart == null && <FaCalendarAlt className="w-3 h-3" />}
                        {formatRelativeDay(subtask.relativeEnd)}
                        </span>
                    )}
                    </>
                ) : (
                    <>
                    {subtask.startDate && (
                        <span className={`flex items-center gap-1 px-2 py-0.5 rounded ${getStartDateColour(subtask.startDate)}`}>
                        <FaCalendarAlt className="w-3 h-3" />
                        {formatDateUK(subtask.startDate)}
                        </span>
                    )}
                    {subtask.startDate && subtask.dueDate && (
                        <span className="text-gray-600">{'\u2192'}</span>
                    )}
                    {subtask.dueDate && (
                        <span className={`flex items-center gap-1 px-2 py-0.5 rounded font-medium ${getDueDateColour(subtask.dueDate)}`}>
                        {!subtask.startDate && <FaCalendarAlt className="w-3 h-3" />}
                        {formatDateUK(subtask.dueDate)}
                        </span>
                    )}
                    </>
                )}
                {subtask.total_comments > 0 && (
                    <span className="flex items-center gap-1 text-gray-500 ml-auto">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <span>{subtask.total_comments}</span>
                    </span>
                )}
                </div>
                {/* Line 2: Assignee */}
                {subtask.assignedToName && (
                    <div className="mt-1 text-xs">
                    <span className={`text-blue-700 ${isAssignedToMe ? 'font-bold' : ''}`}>
                    {subtask.assignedToName}
                    </span>
                    </div>
                )}
                </div>
                </div>
            );
            })}
            </div>
            </div>
        )}

        {/* Comments Section - For non-meeting subtasks only */}
        {task.mainTaskId && !task.isMeeting && (
            <div>
            <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-medium text-gray-700">
            Comments ({task.comments?.filter(c => !c.deletedAt).length || 0})
            </h3>
            {task.comments?.some(c => c.deletedAt) && (
                <button
                onClick={() => setShowDeletedComments(!showDeletedComments)}
                className="text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1"
                >
                {showDeletedComments ? (
                    <>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Hide Deleted
                    </>
                ) : (
                    <>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                    Show Deleted ({task.comments?.filter(c => c.deletedAt).length})
                    </>
                )}
                </button>
            )}
            </div>

            {/* Existing Comments */}
            {task.comments && task.comments.filter(comment => showDeletedComments || !comment.deletedAt).length > 0 && (
                <div className="space-y-3 mb-4 max-h-[400px] overflow-y-auto">
                {task.comments.filter(comment => showDeletedComments || !comment.deletedAt).map((comment, idx) => (
                    <div key={idx} className={`rounded-lg p-3 border ${comment.deletedAt ? 'bg-red-50 border-red-200 opacity-60' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                    <span className={`font-medium text-sm ${comment.deletedAt ? 'text-red-700' : 'text-gray-900'}`}>{comment.authorName}</span>
                    {comment.deletedAt && (
                        <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-medium">
                        Deleted
                        </span>
                    )}
                    {comment.isClosureComment && (
                        <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                        Closure comment
                        </span>
                    )}
                    <span className="text-xs text-gray-500">
                    {new Date(comment.createdAt).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                    })}
                    </span>
                    </div>
                    {!readOnly && canEditTask && !comment.deletedAt && !comment.isClosureComment && (
                        <div className="flex items-center gap-1">
                        <button
                        onClick={() => {
                            setEditingCommentId(comment.commentId);
                            setEditedCommentText(comment.comment);
                        }}
                        className="text-blue-500 hover:text-blue-700 p-1"
                        title="Edit comment"
                        >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        </button>
                        <button
                        onClick={() => onCommentDelete(task.taskId, comment.commentId)}
                        disabled={deletingComment === comment.commentId}
                        className="text-red-500 hover:text-red-700 p-1 disabled:opacity-50"
                        title="Delete comment"
                        >
                        {deletingComment === comment.commentId ? (
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        )}
                        </button>
                        </div>
                    )}
                    </div>

                    {/* Comment Text or Edit Form */}
                    {editingCommentId === comment.commentId ? (
                        <div className="space-y-2">
                        <textarea
                        value={editedCommentText}
                        onChange={(e) => setEditedCommentText(e.target.value)}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none text-sm"
                        />
                        <div className="flex gap-2">
                        <button
                        onClick={() => handleEditComment(comment.commentId)}
                        disabled={editingComment === comment.commentId}
                        className="px-3 py-1 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1"
                        >
                        {editingComment === comment.commentId ? (
                            <>
                            <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Saving...
                            </>
                        ) : 'Save'}
                        </button>
                        <button
                        onClick={() => {
                            setEditingCommentId(null);
                            setEditedCommentText('');
                        }}
                        disabled={editingComment === comment.commentId}
                        className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300 disabled:opacity-50"
                        >
                        Cancel
                        </button>
                        </div>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.comment}</p>
                    )}
                    </div>
                ))}
                </div>
            )}

            {/* Add Comment */}
            {canEditTask && (
                <div>
                <button
                onClick={() => setShowCommentForm(!showCommentForm)}
                className="text-primary-600 hover:text-primary-800 text-sm font-medium flex items-center gap-1 mb-2"
                >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Comment
                </button>

                {showCommentForm && (
                    <div className={`space-y-2 mb-4 p-3 bg-blue-50 border border-blue-200 rounded ${commentFormClosing ? 'animate-slideUp' : 'animate-slideDown'}`}>
                    <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none text-sm"
                    placeholder="Add a comment..."
                    />
                    <div className="flex gap-2">
                    <button
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || addingComment}
                    className="btn-primary text-sm flex-1"
                    >
                    {addingComment ? 'Adding...' : 'Add Comment'}
                    </button>
                    <button
                    onClick={() => {
                        setCommentFormClosing(true);
                        setTimeout(() => {
                            setShowCommentForm(false);
                            setCommentFormClosing(false);
                            setNewComment('');
                        }, 300);
                    }}
                    className="px-4 py-2 text-gray-700 hover:text-gray-900 text-sm"
                    >
                    Cancel
                    </button>
                    </div>
                    </div>
                )}
                </div>
            )}
            </div>
        )}

        {/* Meeting Accordion - For meeting subtasks: Agenda and Comments panels */}
        {task.mainTaskId && task.isMeeting && (
            <div className="flex-1 flex flex-col min-h-0">
            {/* Expanded Panel - Agenda when meetingExpanded='agenda', Comments when meetingExpanded='comments' */}
            {meetingExpanded === 'agenda' ? (
                /* Agenda Expanded */
                <div className="flex-1 flex flex-col min-h-0 mb-2">
                {isAdminProp ? (
                    <AgendaEditor
                        value={editedTaskData?.agenda ?? task.agenda ?? ''}
                        originalValue={task.agenda ?? ''}
                        onChange={(val) => setEditedTaskData({...editedTaskData, agenda: val})}
                        onSave={async (val) => {
                            await handleSaveChanges({ agenda: val }, 'agenda');
                        }}
                        disabled={savingField === 'agenda'}
                        saving={savingField === 'agenda'}
                    />
                ) : (
                    <>
                    <div className="flex items-center p-2 bg-yellow-50 rounded-t-lg border border-b-0 border-yellow-300">
                        <h3 className="text-sm font-medium text-gray-700">Agenda</h3>
                    </div>
                    <div className="flex-1 bg-gray-50 rounded-b-lg p-4 text-sm border border-yellow-300 wiki-doc max-w-none overflow-y-auto">
                    {task.agenda ? (
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{task.agenda}</ReactMarkdown>
                    ) : (
                        <span className="text-gray-400 italic">No agenda set</span>
                    )}
                    </div>
                    </>
                )}
                </div>
            ) : (
                /* Comments Expanded */
                <div className="flex-1 flex flex-col min-h-0 mb-2">
                <div className="flex justify-between items-center p-2 bg-gray-100 rounded-t-lg border border-b-0 border-gray-300">
                    <h3 className="text-sm font-medium text-gray-700">
                    {task.isClosed ? 'Meeting Outcomes' : 'Comments'} ({task.comments?.filter(c => !c.deletedAt).length || 0})
                    </h3>
                    {task.comments?.some(c => c.deletedAt) && (
                        <button
                        onClick={() => setShowDeletedComments(!showDeletedComments)}
                        className="text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1"
                        >
                        {showDeletedComments ? 'Hide Deleted' : `Show Deleted (${task.comments?.filter(c => c.deletedAt).length})`}
                        </button>
                    )}
                </div>
                <div className="flex-1 border border-gray-300 rounded-b-lg overflow-y-auto">
                    {/* Existing Comments */}
                    {task.comments && task.comments.filter(comment => showDeletedComments || !comment.deletedAt).length > 0 ? (
                        <div className="space-y-3 p-3">
                        {task.comments.filter(comment => showDeletedComments || !comment.deletedAt).map((comment, idx) => (
                            <div key={idx} className={`rounded-lg p-3 border ${comment.deletedAt ? 'bg-red-50 border-red-200 opacity-60' : 'bg-gray-50 border-gray-200'}`}>
                            <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                            <span className={`font-medium text-sm ${comment.deletedAt ? 'text-red-700' : 'text-gray-900'}`}>{comment.authorName}</span>
                            {comment.deletedAt && (
                                <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-medium">Deleted</span>
                            )}
                            {comment.isClosureComment && (
                                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">Closure comment</span>
                            )}
                            <span className="text-xs text-gray-500">
                            {new Date(comment.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </span>
                            </div>
                            {!readOnly && canEditTask && !comment.deletedAt && !comment.isClosureComment && (
                                <div className="flex items-center gap-1">
                                <button onClick={() => { setEditingCommentId(comment.commentId); setEditedCommentText(comment.comment); }} className="text-blue-500 hover:text-blue-700 p-1" title="Edit comment">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                </button>
                                <button onClick={() => onCommentDelete(task.taskId, comment.commentId)} disabled={deletingComment === comment.commentId} className="text-red-500 hover:text-red-700 p-1 disabled:opacity-50" title="Delete comment">
                                {deletingComment === comment.commentId ? (
                                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                )}
                                </button>
                                </div>
                            )}
                            </div>
                            {editingCommentId === comment.commentId ? (
                                <div className="space-y-2">
                                <textarea value={editedCommentText} onChange={(e) => setEditedCommentText(e.target.value)} rows="3" className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none text-sm" />
                                <div className="flex gap-2">
                                <button onClick={() => handleEditComment(comment.commentId)} disabled={editingComment === comment.commentId} className="px-3 py-1 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 disabled:opacity-50">{editingComment === comment.commentId ? 'Saving...' : 'Save'}</button>
                                <button onClick={() => { setEditingCommentId(null); setEditedCommentText(''); }} disabled={editingComment === comment.commentId} className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300 disabled:opacity-50">Cancel</button>
                                </div>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.comment}</p>
                            )}
                            </div>
                        ))}
                        </div>
                    ) : (
                        <div className="p-4 text-sm text-gray-400 italic">No comments yet</div>
                    )}
                    {/* Add Comment */}
                    {canEditTask && (
                        <div className="p-3 border-t border-gray-200">
                        {showCommentForm ? (
                            <div className="space-y-2">
                            <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} rows="3" className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none text-sm" placeholder="Add a comment..." />
                            <div className="flex gap-2">
                            <button onClick={handleAddComment} disabled={!newComment.trim() || addingComment} className="btn-primary text-sm flex-1">{addingComment ? 'Adding...' : 'Add Comment'}</button>
                            <button onClick={() => { setShowCommentForm(false); setNewComment(''); }} className="px-4 py-2 text-gray-700 hover:text-gray-900 text-sm">Cancel</button>
                            </div>
                            </div>
                        ) : (
                            <button onClick={() => setShowCommentForm(true)} className="text-primary-600 hover:text-primary-800 text-sm font-medium flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                            Add Comment
                            </button>
                        )}
                        </div>
                    )}
                </div>
                </div>
            )}

            {/* Collapsed Panel - clickable header at bottom */}
            {meetingExpanded === 'agenda' ? (
                /* Comments Collapsed */
                <button
                onClick={() => setMeetingExpanded('comments')}
                className="flex items-center justify-between p-2 bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-300 cursor-pointer transition"
                >
                <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <span className="text-sm font-medium text-gray-700">
                    {task.isClosed ? 'Meeting Outcomes' : 'Comments'} ({task.comments?.filter(c => !c.deletedAt).length || 0})
                    </span>
                </div>
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
                </button>
            ) : (
                /* Agenda Collapsed */
                <button
                onClick={() => setMeetingExpanded('agenda')}
                className="flex items-center justify-between p-2 bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-300 cursor-pointer transition"
                >
                <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                    <span className="text-sm font-medium text-gray-700">Agenda</span>
                </div>
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
                </button>
            )}
            </div>
        )}
        </div>
        </div>

        {/* Recorded history - ADMIN PORTAL ONLY (Part F of the audit page plan).
            This modal is shared with the client portal, and the trail carries
            the text of soft-deleted comments and colleagues' activity. There is
            no client equivalent of this and there must not be one (D8). */}
        {isAdminProp && (
            <div className="border-t px-6 py-4">
                <button
                    onClick={() => {
                        const next = !historyOpen;
                        setHistoryOpen(next);
                        localStorage.setItem('adminTaskHistoryOpen', String(next));
                    }}
                    className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                    {historyOpen ? <FaChevronDown className="w-3 h-3" /> : <FaChevronRight className="w-3 h-3" />}
                    History
                </button>
                {historyOpen && (
                    <div className="mt-3">
                        <TaskHistory projectId={task.projectId} taskId={task.taskId} />
                    </div>
                )}
            </div>
        )}

        {/* Modal Footer */}
        <div className="border-t p-6 bg-gray-50 flex justify-between items-center">
        <div>
        {(isAdminProp || canUserDelete(task)) && !readOnly && (
            <button
            onClick={() => onDelete(task.taskId, task.projectId)}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors flex items-center gap-2"
            >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete Task
            </button>
        )}
        </div>
        <button
        onClick={onClose}
        disabled={isOperationInProgress}
        className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
        >
        {isOperationInProgress ? 'Please wait...' : 'Close'}
        </button>
        </div>
        </div>
        </div>

        {/* Closure Comment Modal */}
        <ClosureCommentModal
            isOpen={showClosureModal}
            taskTitle={task?.subject || ''}
            isMeeting={task?.isMeeting || false}
            onConfirm={handleClosureConfirm}
            onCancel={handleClosureCancel}
            isSubmitting={isSubmittingClosure}
        />

        {/* Blocked hover popup - fixed position, matches Dashboard/Gantt style */}
        {blockedHover && (
            <div
                className="fixed z-[80] px-3 py-2 text-sm bg-gray-800 text-white rounded-lg max-w-xs pointer-events-none"
                style={{
                    left: blockedHover.x,
                    top: blockedHover.y,
                    transform: 'translate(-50%, -100%)',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2)'
                }}
            >
                <div className="font-bold text-red-400">Blocked by:</div>
                {blockedHover.blockers.map((b, i) => (
                    <div key={i} className="pl-2 text-red-300">
                        {b.type === 'issue'
                            ? `Issue ${b.id}: ${b.title}`
                            : b.parentTitle
                                ? `${b.parentTitle} / ${b.title}`
                                : b.title
                        }
                    </div>
                ))}
                <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800" />
            </div>
        )}
        </>
    );
};

export default TaskDetailsModal;
